import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { GoogleMap, Marker, Polyline, InfoWindow, GroundOverlay, OverlayView, Autocomplete } from '@react-google-maps/api';
import Sidebar from '../components/Sidebar';
import SpotManagement from '../components/SpotManagement';
import CourseTimingUpload from '../components/CourseTimingUpload';
import QuickSpotForm from '../components/QuickSpotForm';
import EventPhotographerAssignment from '../components/EventPhotographerAssignment';
import AssignmentManager from './AssignmentManager';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import EventChat from '@/components/EventChat';
import CircuitDrawer from '@/components/CircuitDrawer';
import FloorPlanEditor from '@/components/FloorPlanEditor';
import PhotoLightbox from '@/components/PhotoLightbox';
import BreakCoverageEditor from '@/components/BreakCoverageEditor';
import SpotPhotosGallery from '@/components/SpotPhotosGallery';
import { canEdit, isPhotoEditor, canViewPhotos } from '@/lib/permissions';
import api from '@/lib/api';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { MapPin, Plus, Clock, Camera, Upload, Trash2, Route, Eye, EyeOff, FileText, Users, ToggleLeft, ToggleRight, Edit, X, Save, FileCheck, ChevronUp, ChevronDown, Globe, Link as LinkIcon } from 'lucide-react';
import RichTextEditor from '../components/RichTextEditor';
import { COMMON_TIMEZONES, getTimezoneOffset, getCurrentTimeInTimezone } from '../utils/timezones';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

// Dynamic map container style based on expand/collapse state
const getMapContainerStyle = (isExpanded) => ({
  width: '100%',
  height: isExpanded ? '1500px' : '600px', // 1500px when expanded (75% of original), 600px when collapsed
  borderRadius: '16px',
  transition: 'height 0.3s ease'
});

const mapOptions = {
  mapTypeControl: true,
  mapTypeControlOptions: {
    style: window.google?.maps?.MapTypeControlStyle?.HORIZONTAL_BAR,
    position: window.google?.maps?.ControlPosition?.TOP_RIGHT,
    mapTypeIds: ['roadmap', 'satellite', 'hybrid', 'terrain']
  },
  streetViewControl: true,
  streetViewControlOptions: {
    position: window.google?.maps?.ControlPosition?.RIGHT_TOP
  },
  zoomControl: true,
  zoomControlOptions: {
    position: window.google?.maps?.ControlPosition?.RIGHT_CENTER
  },
  fullscreenControl: true,
  fullscreenControlOptions: {
    position: window.google?.maps?.ControlPosition?.RIGHT_TOP
  },
  scaleControl: true,
  rotateControl: true,
  gestureHandling: 'greedy'
};

const defaultCenter = { lat: -33.8688, lng: 151.2093 }; // Sydney

export default function EventDetail({ user }) {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [locations, setLocations] = useState([]);
  const [plans, setPlans] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [courseTracks, setCourseTracks] = useState([]);
  const [visibleTracks, setVisibleTracks] = useState({});
  // Floor plans (indoor venue maps)
  const [floorPlans, setFloorPlans] = useState([]);
  const [activeFloorPlan, setActiveFloorPlan] = useState(null);   // currently being edited
  const [floorPlanUploadOpen, setFloorPlanUploadOpen] = useState(false);
  const [floorPlanUploadName, setFloorPlanUploadName] = useState('');
  const [floorPlanUploadFile, setFloorPlanUploadFile] = useState(null);
  const [floorPlanUploading, setFloorPlanUploading] = useState(false);
  const [photographerPins, setPhotographerPins] = useState([]);
  const [spots, setSpots] = useState([]);
  const [courseTimings, setCourseTimings] = useState({ modality_timings: [] });
  const [mapMode, setMapMode] = useState('normal'); // 'normal', 'create_spot', 'select_spot'
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [floorPlanImage, setFloorPlanImage] = useState(null);
  const [floorPlanBounds, setFloorPlanBounds] = useState(null);
  const [openFloorPlan, setOpenFloorPlan] = useState(false);
  
  // New states for toggles and photographer assignment
  const [showSpots, setShowSpots] = useState(true);
  const [showPhotographers, setShowPhotographers] = useState(true);
  const [showSpotLabels, setShowSpotLabels] = useState(true);
  const [eventPhotographers, setEventPhotographers] = useState([]);
  const [photographerAssignmentOpen, setPhotographerAssignmentOpen] = useState(false);
  const [boundsForm, setBoundsForm] = useState({
    north: '',
    south: '',
    east: '',
    west: ''
  });
  const [quickSpotDialog, setQuickSpotDialog] = useState(false);
  const [quickSpotData, setQuickSpotData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDayId, setActiveDayId] = useState('all');
  const [editSpotIdRequest, setEditSpotIdRequest] = useState(null);
  const [openLocation, setOpenLocation] = useState(false);
  const [openPlan, setOpenPlan] = useState(false);
  const [openAssignment, setOpenAssignment] = useState(false);
  const [openGPX, setOpenGPX] = useState(false);
  const [drawingCircuit, setDrawingCircuit] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedPhotographer, setSelectedPhotographer] = useState(null);
  const [draggedPhotographer, setDraggedPhotographer] = useState(null);
  const [selectedPin, setSelectedPin] = useState(null);
  const [mapRef, setMapRef] = useState(null);
  const [mapCenter, setMapCenter] = useState(null); // Store map center to prevent re-centering
  const [mapInitialized, setMapInitialized] = useState(false); // Track if map has been initialized
  const [gpxFiles, setGpxFiles] = useState([]);
  const [locationForm, setLocationForm] = useState({
    name: '',
    address: '',
    lat: null,
    lng: null,
    max_capacity: '',
    coverage_notes: '',
    safety_notes: ''
  });
  const [planName, setPlanName] = useState('');
  const [editingPlan, setEditingPlan] = useState(null);
  const [editPlanName, setEditPlanName] = useState('');
  const [deletingPlan, setDeletingPlan] = useState(null);
  
  // Rich description states
  const [eventDescription, setEventDescription] = useState('');
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [templates, setTemplates] = useState([]);
  
  // Timezone editing states
  const [isEditingTimezone, setIsEditingTimezone] = useState(false);
  const [editedTimezone, setEditedTimezone] = useState('');

  // Share link states
  const [shareLinks, setShareLinks] = useState({});
  const [sharingPhotographerId, setSharingPhotographerId] = useState(null);
  
  // Map collapse state
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [zoom, setZoom] = useState(13);

  useEffect(() => {
    fetchEventData();
    fetchTemplates();
    getUserLocation();
  }, [eventId]);

  // Get user's geolocation on mount
  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userPos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(userPos);
          console.log('📍 User location obtained:', userPos);
        },
        (error) => {
          console.log('❌ Geolocation error, using default location');
          setUserLocation(defaultCenter);
        }
      );
    } else {
      console.log('❌ Geolocation not supported, using default location');
      setUserLocation(defaultCenter);
    }
  };

  // Smart map centering based on spots
  useEffect(() => {
    if (!mapRef || !spots) return;

    // Wait for spots to load first
    if (spots.length === 0 && userLocation) {
      // No spots: center on user location
      console.log('🗺️ No spots - centering on user location:', userLocation);
      setMapCenter(userLocation);
      setZoom(13);
      if (mapRef.panTo) {
        mapRef.panTo(userLocation);
        mapRef.setZoom(13);
      }
    } else if (spots.length === 1) {
      // One spot: center on that spot
      const spot = spots[0];
      if (spot.latitude && spot.longitude) {
        const spotCenter = { lat: spot.latitude, lng: spot.longitude };
        console.log('🗺️ One spot - centering on spot:', spot.name);
        setMapCenter(spotCenter);
        setZoom(15);
        if (mapRef.panTo) {
          mapRef.panTo(spotCenter);
          mapRef.setZoom(15);
        }
      }
    } else if (spots.length > 1) {
      // Multiple spots: fit bounds to show all
      console.log('🗺️ Multiple spots - fitting bounds to show all', spots.length, 'spots');
      const bounds = new window.google.maps.LatLngBounds();
      let validSpots = 0;
      spots.forEach(spot => {
        if (spot.latitude && spot.longitude) {
          bounds.extend({ lat: spot.latitude, lng: spot.longitude });
          validSpots++;
        }
      });
      if (validSpots > 0 && mapRef.fitBounds) {
        mapRef.fitBounds(bounds);
        // Add padding for better view
        setTimeout(() => {
          const currentZoom = mapRef.getZoom();
          if (currentZoom && currentZoom > 16) {
            mapRef.setZoom(16); // Max zoom for multiple spots
          }
        }, 100);
      }
    }
  }, [mapRef, spots, userLocation]);


  const fetchEventData = async () => {
    try {
      const [eventRes, locationsRes, plansRes, photographersRes, coursesRes, spotsRes, timingsRes, eventPhotographersRes, floorPlansRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}`),
        axios.get(`${API}/events/${eventId}/locations`),
        axios.get(`${API}/plans/event/${eventId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/photographers`),
        axios.get(`${API}/events/${eventId}/courses`),
        axios.get(`${API}/spots/event/${eventId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/course-timing/event/${eventId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).catch(() => ({ data: { modality_timings: [] } })),
        axios.get(`${API}/events/${eventId}/photographers`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/events/${eventId}/floor-plans`).catch(() => ({ data: [] })),
      ]);
      setEvent(eventRes.data);
      setLocations(locationsRes.data);
      setPlans(plansRes.data);
      setPhotographers(photographersRes.data);
      setCourseTracks(coursesRes.data);
      setSpots(spotsRes.data);
      setCourseTimings(timingsRes.data);
      setEventPhotographers(eventPhotographersRes.data);
      setFloorPlans(floorPlansRes.data || []);
      
      // Initialize all tracks as visible with explicit true values
      const visibilityState = {};
      coursesRes.data.forEach(track => {
        visibilityState[track.id] = true;
      });
      setVisibleTracks(visibilityState);
      console.log('Initialized visible tracks:', visibilityState);
      
      // Set event description
      setEventDescription(eventRes.data.description_html || '');

      // Fetch share link statuses
      try {
        const shareRes = await axios.get(`${API}/share/status/${eventId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const linksMap = {};
        (shareRes.data || []).forEach(link => {
          linksMap[link.photographer_id] = link;
        });
        setShareLinks(linksMap);
      } catch (e) {
        // Share links not available yet, ignore
      }
    } catch (error) {
      toast.error('Failed to fetch event data');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSharePhotographer = async (photographerId) => {
    setSharingPhotographerId(photographerId);
    try {
      const response = await axios.post(`${API}/share/generate`, {
        event_id: eventId,
        photographer_id: photographerId
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const shareUrl = `${window.location.origin}/share/${response.data.token}`;
      await navigator.clipboard.writeText(shareUrl);
      setShareLinks(prev => ({
        ...prev,
        [photographerId]: response.data
      }));
      toast.success('Link copied! Share via WhatsApp or email.');
    } catch (err) {
      toast.error('Failed to generate share link');
      console.error(err);
    } finally {
      setSharingPhotographerId(null);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/content-templates`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleSaveDescription = async () => {
    try {
      await axios.put(`${API}/events/${eventId}`, {
        description_html: eventDescription
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Event description saved ✓', { duration: 2000 });
      setIsEditingDescription(false);
      fetchEventData();
    } catch (error) {
      toast.error('Failed to save description');
      console.error('Error saving description:', error);
    }
  };

  const handleSaveTimezone = async () => {
    try {
      await axios.put(`${API}/events/${eventId}`, {
        timezone: editedTimezone
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Timezone updated ✓', { duration: 2000 });
      setIsEditingTimezone(false);
      fetchEventData();
    } catch (error) {
      toast.error('Failed to update timezone');
      console.error('Error updating timezone:', error);
    }
  };

  const handleInsertTemplate = (template) => {
    setEventDescription(template.content_html);
    setShowTemplateDialog(false);
    toast.success('Template inserted', { duration: 2000 });
  };

  const handleImageUpload = async (file) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/events/upload-description-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Return relative URL so it works in any environment (preview, production, custom domain)
      return response.data.url;
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
      return null;
    }
  };

  const handleCreateLocation = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/events/${eventId}/locations`, locationForm);
      toast.success('Location added!');
      setOpenLocation(false);
      fetchEventData();
      setLocationForm({
        name: '',
        address: '',
        lat: null,
        lng: null,
        max_capacity: '',
        coverage_notes: '',
        safety_notes: ''
      });
    } catch (error) {
      toast.error('Failed to add location');
    }
  };

  const handleCreatePlan = async () => {
    try {
      const response = await axios.post(`${API}/plans`, { 
        event_id: eventId, 
        name: '' // Empty name triggers auto-generation
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Plan created!');
      setOpenPlan(false);
      
      // Auto-navigate to the newly created plan
      const newPlanId = response.data.id;
      navigate(`/events/${eventId}/plan/${newPlanId}`);
    } catch (error) {
      toast.error('Failed to create plan');
    }
  };

  const handleEditPlan = async (e) => {
    e.preventDefault();
    if (!editingPlan || !editPlanName.trim()) return;

    try {
      await axios.put(`${API}/plans/${editingPlan.id}`, { 
        name: editPlanName.trim() 
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Plan updated ✓', { duration: 2000 });
      setEditingPlan(null);
      setEditPlanName('');
      fetchEventData();
    } catch (error) {
      toast.error('Failed to update plan');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan? This action cannot be undone.')) {
      setDeletingPlan(null);
      return;
    }

    try {
      await axios.delete(`${API}/plans/${planId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Plan deleted ✓', { duration: 2000 });
      setDeletingPlan(null);
      fetchEventData();
    } catch (error) {
      toast.error('Failed to delete plan');
      setDeletingPlan(null);
    }
  };

  // Image Library Functions
  const fetchLibraryImages = async () => {
    try {
      const [imagesRes, packsRes] = await Promise.all([
        axios.get(`${API}/image-library`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/image-packs`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      setLibraryImages(imagesRes.data);
      setLibraryPacks(packsRes.data);
    } catch (error) {
      console.error('Error fetching library images:', error);
      toast.error('Failed to load image library');
    }
  };

  const handleAddLibraryImages = async () => {
    if (selectedLibraryImages.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    try {
      // Get the image URLs from selected images
      const selectedUrls = libraryImages
        .filter(img => selectedLibraryImages.includes(img.id))
        .map(img => img.image_url);

      // Add to editingSpot's reference_images
      const updatedImages = [...(editingSpot.reference_images || []), ...selectedUrls];
      
      // Update spot in backend
      await axios.put(
        `${API}/spots/${editingSpot.id}`,
        { reference_images: updatedImages },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setEditingSpot({ ...editingSpot, reference_images: updatedImages });
      setSelectedLibraryImages([]);
      setShowLibrarySelector(false);
      toast.success(`✅ Added ${selectedLibraryImages.length} images from library`);
      fetchEventData();
    } catch (error) {
      console.error('Error adding library images:', error);
      toast.error('Failed to add images');
    }
  };

  const toggleLibraryImageSelection = (imageId) => {
    setSelectedLibraryImages(prev => 
      prev.includes(imageId) 
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId]
    );
  };


  const handleGPXUpload = async () => {
    if (!gpxFiles || gpxFiles.length === 0) {
      toast.error('Please select GPX files');
      return;
    }

    try {
      const formData = new FormData();
      for (let file of gpxFiles) {
        formData.append('files', file);
      }

      toast.info('Processing GPX files...');
      const response = await axios.post(`${API}/events/${eventId}/courses/upload-gpx`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(response.data.message);
      setOpenGPX(false);
      setGpxFiles([]);
      fetchEventData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload GPX files');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (window.confirm('Delete this course track?')) {
      try {
        await axios.delete(`${API}/courses/${courseId}`);
        toast.success('Course track deleted');
        fetchEventData();
      } catch (error) {
        toast.error('Failed to delete course');
      }
    }
  };

  // ----- Floor plan handlers -----
  const handleFloorPlanFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    setFloorPlanUploadFile(f);
    if (!floorPlanUploadName) setFloorPlanUploadName(f.name.replace(/\.[^.]+$/, ''));
  };

  const submitFloorPlanUpload = async () => {
    if (!floorPlanUploadFile) {
      toast.error('Pick an image first');
      return;
    }
    if (!floorPlanUploadName.trim()) {
      toast.error(t('floorPlan.nameRequired'));
      return;
    }
    setFloorPlanUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', floorPlanUploadFile);
      formData.append('name', floorPlanUploadName.trim());
      const res = await axios.post(`${API}/events/${eventId}/floor-plans`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Floor plan uploaded');
      setFloorPlans((prev) => [...prev, res.data]);
      setFloorPlanUploadOpen(false);
      setFloorPlanUploadFile(null);
      setFloorPlanUploadName('');
      // Open editor straight away so the user can calibrate.
      setActiveFloorPlan(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload floor plan');
    } finally {
      setFloorPlanUploading(false);
    }
  };

  const handleDeleteFloorPlan = async (fp) => {
    if (!window.confirm(t('floorPlan.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/floor-plans/${fp.id}`);
      toast.success(t('floorPlan.deleted'));
      setFloorPlans((prev) => prev.filter((p) => p.id !== fp.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete floor plan');
    }
  };

  const handleFloorPlanUpdated = (updated) => {
    setFloorPlans((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    setActiveFloorPlan((prev) => (prev && prev.id === updated.id ? updated : prev));
  };

  const handleFloorPlanTrackSaved = () => {
    setActiveFloorPlan(null);
    fetchEventData();
  };

  const toggleTrackVisibility = (trackId) => {
    setVisibleTracks(prev => {
      const currentValue = prev[trackId];
      const newValue = !currentValue;
      const newState = { ...prev, [trackId]: newValue };
      console.log(`Toggle track ${trackId}: ${currentValue} -> ${newValue}`);
      console.log('New visible tracks state:', newState);
      return newState;
    });
  };

  const toggleAllTracks = () => {
    const currentValues = Object.values(visibleTracks);
    const allVisible = currentValues.length > 0 && currentValues.every(v => v === true);
    const newState = {};
    courseTracks.forEach(track => {
      newState[track.id] = !allVisible;
    });
    console.log('Toggle all tracks. All visible:', allVisible, 'New state:', newState);
    setVisibleTracks(newState);
  };

  const handlePhotographerDragStart = (e, photographer) => {
    console.log('Photographer selected for positioning:', photographer.name);
    setDraggedPhotographer(photographer);
    setSelectedPhotographer(photographer);
    toast.info(`Click anywhere on map to position ${photographer.name}`);
  };

  // Add state for drag mode
  const [dragMode, setDragMode] = useState(false);
  const [draggedSpot, setDraggedSpot] = useState(null);
  const [draggedPhotographerData, setDraggedPhotographerData] = useState(null);
  const [isDraggingPhotographer, setIsDraggingPhotographer] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') || 'map');
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tab !== activeTab) setActiveTab(tab);
  }, [searchParams]);
  const handleTabChange = (v) => {
    setActiveTab(v);
    const next = new URLSearchParams(searchParams);
    if (v === 'map') next.delete('tab'); else next.set('tab', v);
    setSearchParams(next, { replace: true });
  };

  // ---- Day filter: when activeDayId !== 'all', narrow lists down to that day.
  // Items without an event_day_id are treated as "applies to all days" and
  // remain visible.
  const filteredSpots = useMemo(() => {
    if (activeDayId === 'all') return spots;
    return spots.filter((s) => !s.event_day_id || s.event_day_id === activeDayId);
  }, [spots, activeDayId]);

  const filteredPlans = useMemo(() => {
    if (activeDayId === 'all') return plans;
    return plans.filter((p) => {
      const ids = p.event_day_ids;
      if (!ids || ids.length === 0) return true;  // covers all days
      return ids.includes(activeDayId);
    });
  }, [plans, activeDayId]);

  const filteredCourseTracks = useMemo(() => {
    if (activeDayId === 'all') return courseTracks;
    return courseTracks.filter((c) => !c.event_day_id || c.event_day_id === activeDayId);
  }, [courseTracks, activeDayId]);
  const [searchAutocomplete, setSearchAutocomplete] = useState(null);
  const [searchedPlace, setSearchedPlace] = useState(null);
  const [editingSpot, setEditingSpot] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showLibrarySelector, setShowLibrarySelector] = useState(false);
  const [libraryImages, setLibraryImages] = useState([]);
  const [libraryPacks, setLibraryPacks] = useState([]);
  const [selectedLibraryPack, setSelectedLibraryPack] = useState('All');
  const [librarySearchTerm, setLibrarySearchTerm] = useState('');
  const [selectedLibraryImages, setSelectedLibraryImages] = useState([]);

  // Fetch library images when library selector opens
  useEffect(() => {
    if (showLibrarySelector) {
      fetchLibraryImages();
      setSelectedLibraryImages([]);
    }
  }, [showLibrarySelector]);


  // Modality presets by event type
  const modalityPresets = {
    sports: { 'Sports': ['Run', 'Swimming', 'Bike', 'Rowing', 'Finish Line', 'Start Line', 'Transition Zone', 'Aid Station'] },
    fitness: { 'Fitness/Hyrox': ['SKIERG', 'SLED PUSH', 'SLED PULL', 'BURPEE BROAD JUMPS', 'ROWING', 'FARMERS CARRY', 'LUNGES', 'WALL BALLS'] },
    wedding: { 'Wedding': ['Ceremony', 'First Dance', 'Reception', 'Getting Ready', 'Portraits', 'Group Photos', 'Details', 'Cake Cutting'] },
    real_estate: { 'Real Estate': ['Exterior', 'Interior', 'Kitchen', 'Living Room', 'Bedroom', 'Bathroom', 'Aerial/Drone', 'Twilight'] },
    corporate: { 'Corporate': ['Headshots', 'Product', 'Office', 'Meeting Room', 'Event Space', 'Branding'] },
    events: { 'Events': ['Ceremony', 'Reception', 'Stage', 'Backstage', 'VIP Area', 'Entrance', 'Red Carpet'] },
    general: {
      'Common': ['Wide Shot', 'Detail', 'Portrait', 'Group', 'Action', 'Candid', 'Establishing Shot'],
      'Sports': ['Run', 'Swimming', 'Bike'],
      'Events': ['Ceremony', 'Stage', 'Reception'],
    }
  };

  // Get modalities for current event type
  const getEventModalities = () => {
    const eventType = event?.event_type || 'general';
    const presets = modalityPresets[eventType] || modalityPresets.general;
    // Also include any custom modalities from the event
    const custom = event?.custom_modalities || [];
    if (custom.length > 0) {
      return { ...presets, 'Custom': custom };
    }
    return presets;
  };

  // Gear recommendations by scene type
  const commonGearBySport = {
    'Run': ['70-200mm f/2.8', '24-70mm f/2.8', 'Wide angle 16-35mm', 'Telephoto 300mm+'],
    'Swimming': ['70-200mm f/2.8', 'Telephoto 300mm+', 'Underwater housing'],
    'Bike': ['70-200mm f/2.8', 'Telephoto 300mm+', 'Drone'],
    'Ceremony': ['24-70mm f/2.8', '70-200mm f/2.8', '35mm f/1.4', 'Flash/Strobe'],
    'Reception': ['24-70mm f/2.8', '35mm f/1.4', 'Flash/Strobe'],
    'Portraits': ['85mm f/1.4', '70-200mm f/2.8', 'Reflector'],
    'Exterior': ['Wide angle 16-35mm', '24-70mm f/2.8', 'Drone', 'Tripod'],
    'Interior': ['Wide angle 16-35mm', 'Tripod', 'Flash/Strobe', 'Light stands'],
    'Headshots': ['85mm f/1.4', '70-200mm f/2.8', 'Flash/Strobe', 'Backdrop'],
    'General': ['24-70mm f/2.8', '70-200mm f/2.8', 'Wide angle 16-35mm', 'Flash/Strobe', 'Drone']
  };

  // Get suggested gear based on selected modalities
  const getSuggestedGear = (modalities) => {
    const allSuggestions = new Set();
    (modalities || []).forEach(modality => {
      const gearList = commonGearBySport[modality] || commonGearBySport['General'];
      gearList.forEach(gear => allSuggestions.add(gear));
    });
    return Array.from(allSuggestions);
  };

  // Get timing suggestions based on selected modalities
  const getTimingSuggestions = (modalities) => {
    const suggestions = [];
    if (courseTimings && courseTimings.modality_timings) {
      courseTimings.modality_timings.forEach(modalityTiming => {
        if ((modalities || []).includes(modalityTiming.modality)) {
          const timeParts = [];
          if (modalityTiming.start_time) timeParts.push(`Start: ${modalityTiming.start_time}`);
          if (modalityTiming.first_finisher_time) timeParts.push(`First: ${modalityTiming.first_finisher_time}`);
          if (modalityTiming.last_finisher_time) timeParts.push(`Last: ${modalityTiming.last_finisher_time}`);
          if (modalityTiming.cutoff_time) timeParts.push(`Cutoff: ${modalityTiming.cutoff_time}`);
          
          if (timeParts.length > 0) {
            suggestions.push({
              modality: modalityTiming.modality,
              timeInfo: timeParts.join(' | '),
              data: modalityTiming
            });
          }
        }
      });
    }
    return suggestions;
  };

  // Reverse geocode to get address from coordinates
  const reverseGeocode = async (lat, lng) => {
    try {
      console.log(`🔍 Reverse geocoding: ${lat}, ${lng}`);
      // Use fetch instead of axios to avoid sending Authorization headers
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_KEY}`
      );
      
      const data = await response.json();
      console.log('📍 Geocoding response:', data);
      
      if (data.results && data.results.length > 0) {
        const address = data.results[0].formatted_address;
        console.log(`✅ Geocoded address: ${address}`);
        return address;
      }
      console.log('⚠️ No results from geocoding');
      return null;
    } catch (error) {
      console.error('❌ Reverse geocoding error:', error);
      return null;
    }
  };

  const handleMapClick = async (e) => {
    // If photographer is being dragged, create new spot at click location
    if (draggedPhotographerData) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      
      try {
        // Reverse geocode to get address
        const address = await reverseGeocode(lat, lng);
        
        // Create new spot with photographer pre-assigned
        const newSpot = {
          event_id: eventId,
          name: `${draggedPhotographerData.name}'s Spot`,
          description: `Spot for ${draggedPhotographerData.name}`,
          address: address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
          lat,
          lng,
          coverage_role: 'Action',
          priority: 'medium',
          flash_required: false,
          use_led_flash: false,
          break_time_1: '',
          break_time_2: '',
          modalities: [],
          arrive_time: '',
          leave_time: '',
          first_athlete_time: '',
          last_athlete_time: '',
          cutoff_time: '',
          required_gear: draggedPhotographerData.personal_gear?.map(g => g.name) || [],
          assigned_photographers: [draggedPhotographerData.id],
          max_photographers: 1
        };
        
        const response = await axios.post(
          `${API}/spots`,
          newSpot,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        
        toast.success(`✅ Created spot for ${draggedPhotographerData.name} at ${address || 'selected location'}`);
        fetchEventData();
        setDraggedPhotographerData(null);
        return;
      } catch (error) {
        console.error('Error creating spot:', error);
        toast.error('Failed to create spot');
        setDraggedPhotographerData(null);
        return;
      }
    }
    
    // If we're in drag mode, handle spot placement
    if (dragMode && draggedSpot) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      
      try {
        console.log(`📍 Setting location for spot: ${draggedSpot.name}`);
        console.log(`📍 Coordinates: ${lat}, ${lng}`);
        
        // First, reverse geocode to get the address
        const address = await reverseGeocode(lat, lng);
        console.log(`📬 Address to save: ${address}`);
        
        // Update spot with coordinates AND address in a single call
        const updatePayload = { 
          lat: lat,
          lng: lng,
          address: address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`
        };
        
        console.log('📤 Sending update:', updatePayload);
        
        const response = await axios.put(
          `${API}/spots/${draggedSpot.id}`,
          updatePayload,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        
        console.log('📥 Update response:', response.data);
        
        if (address) {
          toast.success(`📍 ${draggedSpot.name} positioned at ${address}`);
        } else {
          toast.success(`📍 ${draggedSpot.name} location set to ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
        }
        
        fetchEventData();
        
        // Reset drag mode
        setDragMode(false);
        setDraggedSpot(null);
        return;
      } catch (error) {
        console.error('❌ Error updating spot location:', error);
        toast.error('Failed to update spot location');
        setDragMode(false);
        setDraggedSpot(null);
        return;
      }
    }

    // Rest of existing handleMapClick logic
    if (mapMode === 'create_spot') {
      // Get coordinates from click
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      
      // Reverse geocode to get address
      try {
        const address = await reverseGeocode(lat, lng);
        
        // Open quick spot form with pre-filled coordinates
        setQuickSpotData({
          lat,
          lng,
          address: address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`
        });
        setQuickSpotDialog(true);
        setMapMode('normal'); // Exit create mode
        
        toast.success(`📍 Spot location selected at ${address || 'coordinates'}`);
      } catch (error) {
        console.error('Error geocoding:', error);
        // Even if geocoding fails, still create the spot
        setQuickSpotData({
          lat,
          lng,
          address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`
        });
        setQuickSpotDialog(true);
        setMapMode('normal');
      }
      return;
    }
    
    // Default mode - photographer placement
    console.log('Selected photographer:', selectedPhotographer ? selectedPhotographer.name : 'none');
    console.log('Dragged photographer:', draggedPhotographer ? draggedPhotographer.name : 'none');
    
    const photographerToPlace = draggedPhotographer || selectedPhotographer;
    
    if (photographerToPlace) {
      console.log('Creating pin for:', photographerToPlace.name, 'at:', lat, lng);
      
      // Create a new photographer pin
      const newPin = {
        id: `pin-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        photographer: photographerToPlace,
        lat,
        lng,
        timestamp: new Date().toISOString()
      };
      
      setPhotographerPins(prev => {
        const updated = [...prev, newPin];
        console.log('Updated photographer pins:', updated);
        return updated;
      });
      
      toast.success(`📍 ${photographerToPlace.name} positioned at ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
      
      // Reset both states
      setDraggedPhotographer(null);
      setSelectedPhotographer(null);
    } else {
      console.log('No photographer selected');
    }
  };

  const handleMapLoad = (map) => {
    setMapRef(map);
    console.log('Map loaded');
  };

  const removePhotographerPin = (pinId) => {
    setPhotographerPins(prev => {
      const updated = prev.filter(p => p.id !== pinId);
      console.log('Removed pin, remaining:', updated);
      return updated;
    });
    toast.info('Photographer pin removed');
  };
  
  const handleFloorPlanUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFloorPlanImage(event.target.result);
        setOpenFloorPlan(true);
        toast.success('Floor plan uploaded! Set the map bounds for overlay.');
      };
      reader.readAsDataURL(file);
    } else {
      toast.error('Please upload an image file');
    }
  };
  
  const handleSetFloorPlanBounds = () => {
    const bounds = {
      north: parseFloat(boundsForm.north),
      south: parseFloat(boundsForm.south),
      east: parseFloat(boundsForm.east),
      west: parseFloat(boundsForm.west)
    };
    
    if (isNaN(bounds.north) || isNaN(bounds.south) || isNaN(bounds.east) || isNaN(bounds.west)) {
      toast.error('Please enter valid coordinates');
      return;
    }
    
    setFloorPlanBounds(bounds);
    setOpenFloorPlan(false);
    toast.success('Floor plan overlay set!');
  };
  
  const handleSpotClick = (spot) => {
    setSelectedSpot(spot);
    toast.info(`Selected: ${spot.name}`);
  };
  
  const handleQuickCreateSpot = async (spotData) => {
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };
    
    try {
      // Extract assigned photographers before creating spot
      const assignedPhotographers = spotData.assigned_photographers || [];
      const spotPayload = { ...spotData };
      delete spotPayload.assigned_photographers; // Remove from payload as backend will handle separately
      
      // Create the spot
      const response = await axios.post(`${API}/spots`, { ...spotPayload, event_id: eventId }, config);
      const newSpotId = response.data.id;
      
      // Assign photographers if any were selected
      if (assignedPhotographers.length > 0) {
        for (const photographerId of assignedPhotographers) {
          try {
            await axios.post(`${API}/spots/${newSpotId}/assign`, {
              spot_id: newSpotId,
              photographer_id: photographerId
            }, config);
          } catch (err) {
            console.error('Failed to assign photographer:', err);
          }
        }
        toast.success(`Spot created with ${assignedPhotographers.length} photographer(s) assigned!`);
      } else {
        toast.success('Spot created successfully!');
      }
      
      setQuickSpotDialog(false);
      fetchEventData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create spot');
    }
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar user={user} />
        <div className="main-content flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <Sidebar user={user} />
      <div className="main-content flex-1 w-full overflow-x-hidden">
        <div className="page-header">
          <div>
            <h1 className="page-title">{event.name}</h1>
            <p className="page-subtitle flex items-center gap-1">
              <span>{event.client} • {event.date} • {event.start_time} - {event.end_time}</span>
              <span className="flex items-center gap-1 text-slate-600">
                <Globe className="w-3 h-3" />
                {event.timezone}
              </span>
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => setPhotographerAssignmentOpen(true)}
              variant="outline"
              size="sm"
            >
              <Users className="w-4 h-4 mr-2" />
              Assign Photographers ({eventPhotographers.length})
            </Button>
          </div>
        </div>

        {Array.isArray(event.days) && event.days.length > 1 && (
          <div className="mb-3 flex items-center gap-2 overflow-x-auto pb-1">
            <span className="text-xs text-slate-500 flex-shrink-0">{t('events.dayFilter', { defaultValue: 'Day:' })}</span>
            <button
              type="button"
              onClick={() => setActiveDayId('all')}
              className={`text-xs px-3 py-1.5 rounded-full border whitespace-nowrap transition ${
                activeDayId === 'all'
                  ? 'bg-emerald-600 text-white border-emerald-700'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {t('events.dayAll', { defaultValue: 'All days' })}
            </button>
            {event.days.map((day, idx) => (
              <button
                key={day.id}
                type="button"
                onClick={() => setActiveDayId(day.id)}
                className={`text-xs px-3 py-1.5 rounded-full border whitespace-nowrap transition ${
                  activeDayId === day.id
                    ? 'bg-emerald-600 text-white border-emerald-700'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
                title={`${day.date} ${day.start_time}–${day.end_time}`}
              >
                {day.label || t('events.dayN', { defaultValue: 'Day {{n}}', n: idx + 1 })} · {day.date}
              </button>
            ))}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={handleTabChange} className="flex flex-col">
          <TabsList className="bg-white/80 backdrop-blur-sm p-1 rounded-lg shadow-sm flex-shrink-0 w-full overflow-x-auto flex-nowrap">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            {!isPhotoEditor(user) && <TabsTrigger value="map" data-testid="tab-map">Map</TabsTrigger>}
            {!isPhotoEditor(user) && <TabsTrigger value="spots" data-testid="tab-spots">Photography Spots ({filteredSpots.length})</TabsTrigger>}
            {!isPhotoEditor(user) && <TabsTrigger value="assignments" data-testid="tab-assignments">Assignment Manager</TabsTrigger>}
            {!isPhotoEditor(user) && <TabsTrigger value="timing" data-testid="tab-timing">Course Timing ({courseTimings?.modality_timings?.length || 0})</TabsTrigger>}
            {!isPhotoEditor(user) && <TabsTrigger value="plans" data-testid="tab-plans">Plans ({filteredPlans.length})</TabsTrigger>}
            {canViewPhotos(user) && <TabsTrigger value="photos" data-testid="tab-photos">Photos</TabsTrigger>}
            {!isPhotoEditor(user) && <TabsTrigger value="chat" data-testid="tab-chat">Chat</TabsTrigger>}
          </TabsList>

          <TabsContent value="overview" className="space-y-6 flex-1 overflow-y-auto">
            <Card className="glass-card p-6 group">
              <h2 className="text-xl font-semibold mb-4">Event Details</h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-600 mb-1">Client</p>
                  <p className="font-medium">{event.client}</p>
                </div>
                <div>
                  <p className="text-slate-600 mb-1">Date</p>
                  <p className="font-medium">{event.date}</p>
                </div>
                <div>
                  <p className="text-slate-600 mb-1">Time</p>
                  <p className="font-medium">{event.start_time} - {event.end_time}</p>
                </div>
                <div>
                  <p className="text-slate-600 mb-1 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Timezone
                  </p>
                  {isEditingTimezone ? (
                    <div className="flex items-center gap-2">
                      <select
                        value={editedTimezone}
                        onChange={(e) => setEditedTimezone(e.target.value)}
                        className="border rounded px-2 py-1 text-sm flex-1"
                      >
                        {COMMON_TIMEZONES.map(tz => (
                          <option key={tz.value} value={tz.value}>
                            {tz.label} - {getTimezoneOffset(tz.value)}
                          </option>
                        ))}
                      </select>
                      <Button size="sm" variant="ghost" onClick={handleSaveTimezone}>
                        <Save className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setIsEditingTimezone(false)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <p className="font-medium">
                        {event.timezone} ({getTimezoneOffset(event.timezone)})
                      </p>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => {
                          setEditedTimezone(event.timezone);
                          setIsEditingTimezone(true);
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                  <p className="text-xs text-slate-500 mt-1">
                    Current time: {getCurrentTimeInTimezone(event.timezone)}
                  </p>
                </div>
                <div>
                  <p className="text-slate-600 mb-1">Status</p>
                  <span className={`status-badge status-${event.status}`}>{event.status}</span>
                </div>
              </div>
            </Card>

            <div className="grid grid-cols-3 gap-4">
              <Card className="glass-card p-6">
                <MapPin className="w-8 h-8 text-emerald-600 mb-3" />
                <p className="text-2xl font-bold">{locations.length}</p>
                <p className="text-slate-600 text-sm">Locations</p>
              </Card>
              <Card className="glass-card p-6">
                <Camera className="w-8 h-8 text-blue-600 mb-3" />
                <p className="text-2xl font-bold">{plans.length}</p>
                <p className="text-slate-600 text-sm">Coverage Plans</p>
              </Card>
              <Card className="glass-card p-6">
                <Clock className="w-8 h-8 text-purple-600 mb-3" />
                <p className="text-2xl font-bold">0</p>
                <p className="text-slate-600 text-sm">Time Slots</p>
              </Card>
            </div>

            {/* Event Description with Rich Text Editor */}
            <Card className="glass-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Event Description</h2>
                <div className="flex gap-2">
                  {isEditingDescription ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowTemplateDialog(true)}
                      >
                        <FileCheck className="w-4 h-4 mr-2" />
                        Insert Template
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setIsEditingDescription(false);
                          setEventDescription(event?.description_html || '');
                        }}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveDescription}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setIsEditingDescription(true)}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Description
                    </Button>
                  )}
                </div>
              </div>

              {isEditingDescription ? (
                <RichTextEditor
                  content={eventDescription}
                  onChange={setEventDescription}
                  onImageUpload={handleImageUpload}
                  placeholder="Add event description with rich formatting, images, videos, and more..."
                />
              ) : (
                <div className="prose max-w-none">
                  {eventDescription ? (
                    <div dangerouslySetInnerHTML={{ __html: eventDescription }} />
                  ) : (
                    <p className="text-slate-400 italic">No description added yet. Click "Edit Description" to add one.</p>
                  )}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Template Selection Dialog */}
          <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Insert Content Template</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {templates.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">
                    No templates available. Create one from the Content Templates page.
                  </p>
                ) : (
                  templates.map((template) => (
                    <Card
                      key={template.id}
                      className="p-4 cursor-pointer hover:border-emerald-500 transition-all"
                      onClick={() => handleInsertTemplate(template)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold mb-1">{template.name}</h3>
                          <div 
                            className="text-sm text-slate-600 line-clamp-2"
                            dangerouslySetInnerHTML={{ 
                              __html: template.content_html.replace(/<[^>]*>/g, ' ').substring(0, 150) + '...' 
                            }}
                          />
                        </div>
                        <Button size="sm" variant="outline">
                          Insert
                        </Button>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </DialogContent>
          </Dialog>

          <TabsContent value="map" forceMount className={`flex-1 overflow-hidden ${activeTab !== 'map' ? 'hidden' : ''}`}>
            {drawingCircuit && (
              <CircuitDrawer
                eventId={eventId}
                eventDayId={null}
                center={(() => {
                  const firstSpot = spots.find((s) => s.lat && s.lng);
                  if (firstSpot) return { lat: firstSpot.lat, lng: firstSpot.lng };
                  const firstLoc = locations.find((l) => l.lat && l.lng);
                  if (firstLoc) return { lat: firstLoc.lat, lng: firstLoc.lng };
                  return { lat: -33.8688, lng: 151.2093 };
                })()}
                defaultZoom={15}
                onSaved={() => { setDrawingCircuit(false); fetchEventData(); }}
                onCancel={() => setDrawingCircuit(false)}
              />
            )}
            {activeFloorPlan && (
              <FloorPlanEditor
                eventId={eventId}
                floorPlan={activeFloorPlan}
                onSaved={handleFloorPlanTrackSaved}
                onCancel={() => setActiveFloorPlan(null)}
                onPlanUpdated={handleFloorPlanUpdated}
              />
            )}
            <Card className="glass-card p-3 h-full flex flex-col">
              {/* Compact header bar */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded text-blue-700 text-xs font-medium">
                      <Users className="w-3 h-3" /> {eventPhotographers.length} photographers
                    </span>
                    <span className="flex items-center gap-1 px-2 py-1 bg-green-50 rounded text-green-700 text-xs font-medium">
                      <MapPin className="w-3 h-3" /> {spots.filter(s => s.lat && s.lng).length} spots
                    </span>
                    {courseTracks.length > 0 && (
                      <span className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded text-gray-600 text-xs font-medium">
                        {courseTracks.length} tracks
                      </span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsMapExpanded(!isMapExpanded)}
                    className="text-gray-500 hover:text-gray-700 text-xs h-7"
                  >
                    {isMapExpanded ? (
                      <><ChevronUp className="w-3 h-3 mr-1" /> Hide panels</>
                    ) : (
                      <><ChevronDown className="w-3 h-3 mr-1" /> Show panels</>
                    )}
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="h-7 text-xs"
                    onClick={() => setDrawingCircuit(true)}>
                    <Route className="w-3 h-3 mr-1" />
                    Draw circuit
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => document.getElementById('floor-plan-upload').click()}>
                    <Upload className="w-3 h-3 mr-1" />
                    Floor Plan
                  </Button>
                  <input
                    id="floor-plan-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleFloorPlanUpload}
                    className="hidden"
                  />
                  <Dialog open={openGPX} onOpenChange={setOpenGPX}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="h-7 text-xs" data-testid="upload-gpx-btn">
                        <Route className="w-3 h-3 mr-1" />
                        GPX
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Import GPX Course Files</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-6">
                          <Upload className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                          <input
                            type="file"
                            accept=".gpx"
                            multiple
                            onChange={(e) => setGpxFiles(Array.from(e.target.files))}
                            className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                            data-testid="gpx-file-input"
                          />
                          <p className="text-xs text-slate-500 mt-2 text-center">Select one or multiple GPX files</p>
                        </div>

                        {gpxFiles.length > 0 && (
                          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                            <p className="text-sm text-emerald-900 font-medium mb-2">
                              Selected {gpxFiles.length} file(s):
                            </p>
                            {gpxFiles.map((file, idx) => (
                              <p key={idx} className="text-xs text-emerald-800">• {file.name}</p>
                            ))}
                          </div>
                        )}

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                          <p className="font-medium text-blue-900 mb-1">About GPX Files:</p>
                          <p className="text-blue-800 text-xs">
                            • Upload race track, circuit, or route GPX files<br/>
                            • Each file will be shown as a separate colored line<br/>
                            • Perfect for visualizing event courses and stages<br/>
                            • Export GPX files from Strava, Garmin, or route planning tools
                          </p>
                        </div>

                        <div className="flex gap-3">
                          <Button variant="outline" onClick={() => { setOpenGPX(false); setGpxFiles([]); }} data-testid="cancel-gpx-btn">
                            Cancel
                          </Button>
                          <Button onClick={handleGPXUpload} className="btn-gradient" disabled={gpxFiles.length === 0} data-testid="upload-gpx-submit-btn">
                            <Upload className="w-4 h-4 mr-2" />
                            Import Course(s)
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              {/* Three Column Row Above Map - collapsible */}
              {isMapExpanded && <div className="grid grid-cols-12 gap-3 mb-3 animate-in fade-in duration-200">
                {/* Assigned Photographers - 35% */}
                <div className="col-span-4">
                  {eventPhotographers.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 h-full">
                      <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2 text-sm">
                        <Users className="h-4 w-4" />
                        Assigned Photographers ({eventPhotographers.length})
                      </h4>
                      <p className="text-xs text-blue-700 mb-2">
                        Drag photographers onto spots or map to assign them.
                      </p>
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {eventPhotographers.map((photographer) => {
                          const assignedSpots = spots.filter(spot => 
                            spot.assigned_photographers?.includes(photographer.id)
                          );
                          
                          const gearTooltip = photographer.personal_gear && photographer.personal_gear.length > 0
                            ? photographer.personal_gear.map(g => 
                                typeof g === 'object' ? `${g.name} (${g.type})` : g
                              ).join('\n')
                            : 'No gear listed';
                          
                          return (
                            <div
                              key={photographer.id}
                              draggable
                              onDragStart={(e) => {
                                e.dataTransfer.setData('photographer', JSON.stringify(photographer));
                                setIsDraggingPhotographer(true);
                                toast.info(`Dragging ${photographer.name}`);
                              }}
                              onDragEnd={() => {
                                setIsDraggingPhotographer(false);
                              }}
                              className="p-2 rounded-lg border-2 cursor-move transition-all text-sm bg-white hover:border-blue-300 hover:shadow-md"
                              data-testid={`photographer-item-${photographer.id}`}
                              title={gearTooltip}
                            >
                              <div className="flex items-start gap-2">
                                <div className="text-blue-500 mt-0.5">⋮⋮</div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-1">
                                    <p className="font-medium text-xs">{photographer.name}</p>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSharePhotographer(photographer.id);
                                      }}
                                      disabled={sharingPhotographerId === photographer.id}
                                      className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs hover:bg-blue-100 transition-colors shrink-0"
                                      title="Copy share link"
                                    >
                                      {sharingPhotographerId === photographer.id ? (
                                        <span className="animate-spin w-3 h-3 border border-blue-500 border-t-transparent rounded-full inline-block"></span>
                                      ) : (
                                        <>
                                          {shareLinks[photographer.id] && (
                                            <span className={`w-1.5 h-1.5 rounded-full inline-block ${shareLinks[photographer.id].opened_count > 0 ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
                                          )}
                                          <LinkIcon className="w-3 h-3 text-blue-500" />
                                        </>
                                      )}
                                    </button>
                                  </div>
                                  <p className="text-xs text-slate-600 truncate">{photographer.email}</p>
                                  
                                  {assignedSpots.length > 0 && (
                                    <div className="mt-2 space-y-1">
                                      {assignedSpots.map(spot => (
                                        <div key={spot.id} className="text-xs bg-emerald-50 rounded px-1.5 py-0.5 border border-emerald-200">
                                          <div className="font-medium text-emerald-700 flex items-center gap-1">
                                            <MapPin className="w-3 h-3" />
                                            {spot.name}
                                          </div>
                                          {(spot.arrive_time || spot.leave_time) && (
                                            <div className="text-emerald-600 flex items-center gap-1 mt-0.5">
                                              <Clock className="w-3 h-3" />
                                              {spot.arrive_time && spot.leave_time 
                                                ? `${spot.arrive_time} - ${spot.leave_time}`
                                                : spot.arrive_time || spot.leave_time
                                              }
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {assignedSpots.length === 0 && photographer.personal_gear && photographer.personal_gear.length > 0 && (
                                    <p className="text-xs text-blue-600 mt-1">
                                      📷 {photographer.personal_gear.length} gear items
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Positioned Spots - 35% */}
                <div className="col-span-4">
                  {spots.filter(spot => spot.lat && spot.lng).length > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 h-full">
                      <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2 text-sm">
                        <MapPin className="h-4 w-4" />
                        Positioned Spots ({spots.filter(spot => spot.lat && spot.lng).length})
                      </h4>
                      <p className="text-xs text-green-700 mb-2">
                        {isDraggingPhotographer
                          ? '📌 Drop a photographer here to assign them to a spot.'
                          : 'Click any spot to navigate to its location on the map.'}
                      </p>
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {spots
                          .filter(spot => spot.lat && spot.lng)
                          .map((spot) => {
                            const assignedPhotographers = eventPhotographers.filter(p => 
                              spot.assigned_photographers?.includes(p.id)
                            );
                            
                            return (
                              <div
                                key={spot.id}
                                className={`p-2 bg-white rounded border hover:shadow-sm transition-all cursor-pointer ${isDraggingPhotographer ? 'border-dashed border-blue-300 hover:border-blue-500 hover:bg-blue-50' : ''}`}
                                onClick={() => {
                                  if (mapRef.current && spot.lat && spot.lng) {
                                    mapRef.current.panTo({ lat: spot.lat, lng: spot.lng });
                                    mapRef.current.setZoom(16);
                                  }
                                }}
                                onDragOver={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  e.currentTarget.classList.add('ring-2', 'ring-blue-500', 'bg-blue-50');
                                }}
                                onDragLeave={(e) => {
                                  e.currentTarget.classList.remove('ring-2', 'ring-blue-500', 'bg-blue-50');
                                }}
                                onDrop={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  e.currentTarget.classList.remove('ring-2', 'ring-blue-500', 'bg-blue-50');
                                  const photographerData = e.dataTransfer.getData('photographer');
                                  if (photographerData) {
                                    const photographer = JSON.parse(photographerData);
                                    try {
                                      await axios.post(
                                        `${API}/spots/${spot.id}/assign`,
                                        { spot_id: spot.id, photographer_id: photographer.id },
                                        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                                      );
                                      toast.success(`${photographer.name} assigned to ${spot.name}`);
                                      setDraggedPhotographerData(null);
                                      setIsDraggingPhotographer(false);
                                      fetchEventData();
                                    } catch (error) {
                                      const msg = error.response?.data?.detail || 'Failed to assign';
                                      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
                                    }
                                  }
                                }}
                              >
                                <div className="font-medium text-gray-900 text-xs">{spot.name}</div>
                                <div className="text-xs text-gray-600">
                                  {spot.modalities.length > 0 && (
                                    <span className="text-emerald-600">
                                      {spot.modalities.join(', ')}
                                    </span>
                                  )}
                                </div>
                                {assignedPhotographers.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {assignedPhotographers.map((photographer) => (
                                      <span key={photographer.id} className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                                        {photographer.name.split(' ')[0]}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Course Tracks - 30% */}
                <div className="col-span-4">
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 h-full">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-sm text-slate-700">Course Tracks ({filteredCourseTracks.length})</h3>
                      {filteredCourseTracks.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={toggleAllTracks}
                          className="h-6 px-2 text-xs"
                          data-testid="toggle-all-tracks"
                        >
                          {Object.values(visibleTracks).every(v => v) ? (
                            <><EyeOff className="w-3 h-3 mr-1" /> Hide All</>
                          ) : (
                            <><Eye className="w-3 h-3 mr-1" /> Show All</>
                          )}
                        </Button>
                      )}
                    </div>
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {filteredCourseTracks.length === 0 ? (
                        <p className="text-xs text-slate-500 text-center py-3">{courseTracks.length === 0 ? 'No courses imported' : 'No courses for this day'}</p>
                      ) : (
                        filteredCourseTracks.map((track) => (
                          <div
                            key={track.id}
                            className={`p-2 rounded-lg border transition-all ${
                              visibleTracks[track.id]
                                ? 'bg-white border-slate-200'
                                : 'bg-slate-50 border-slate-200 opacity-50'
                            }`}
                            data-testid={`course-track-${track.id}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <div 
                                  className="w-4 h-4 rounded flex-shrink-0" 
                                  style={{ backgroundColor: track.color }}
                                ></div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-xs truncate">{track.name}</p>
                                  <p className="text-xs text-slate-500">{track.track_points?.length || 0} points</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleTrackVisibility(track.id)}
                                  className="h-6 w-6 p-0 text-slate-600 hover:text-slate-900"
                                  data-testid={`toggle-track-${track.id}`}
                                >
                                  {visibleTracks[track.id] ? (
                                    <Eye className="w-3 h-3" />
                                  ) : (
                                    <EyeOff className="w-3 h-3" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteCourse(track.id)}
                                  className="h-6 w-6 p-0 text-red-500 hover:text-red-600"
                                  data-testid={`delete-course-${track.id}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>}

              {/* Floor plans (indoor venue maps) */}
              {isMapExpanded && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div>
                      <h3 className="font-semibold text-sm text-slate-700">
                        {t('floorPlan.section')} ({floorPlans.length})
                      </h3>
                      <p className="text-xs text-slate-500">{t('floorPlan.sectionHint')}</p>
                    </div>
                    {canEdit(user, 'events') && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => setFloorPlanUploadOpen(true)}
                        data-testid="upload-floor-plan-btn"
                      >
                        <Upload className="w-3 h-3 mr-1" />
                        {t('floorPlan.upload')}
                      </Button>
                    )}
                  </div>
                  {floorPlans.length === 0 ? (
                    <p className="text-xs text-slate-500 text-center py-3">{t('floorPlan.noPlans')}</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {floorPlans.map((fp) => {
                        const tracksHere = courseTracks.filter((tr) => tr.is_floor_plan && tr.floor_plan_id === fp.id);
                        const imgSrc = fp.image_url?.startsWith('http')
                          ? fp.image_url
                          : `${BACKEND_URL}${fp.image_url}`;
                        return (
                          <div
                            key={fp.id}
                            className="flex gap-2 p-2 bg-white border border-slate-200 rounded-lg"
                            data-testid={`floor-plan-${fp.id}`}
                          >
                            <img
                              src={imgSrc}
                              alt={fp.name}
                              className="w-16 h-16 object-cover rounded border border-slate-200 flex-shrink-0 bg-slate-100"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-xs truncate">{fp.name}</p>
                              <p className="text-xs text-slate-500">
                                {fp.scale_meters_per_pixel
                                  ? <>{t('floorPlan.calibrated')} · {(fp.scale_meters_per_pixel * 100).toFixed(2)} cm/px</>
                                  : <span className="text-amber-600">{t('floorPlan.notCalibrated')}</span>}
                              </p>
                              <p className="text-xs text-slate-500">{tracksHere.length} tracks</p>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {canEdit(user, 'events') && (
                                  <>
                                    <Button
                                      size="sm" variant="outline"
                                      className="h-6 px-2 text-xs"
                                      onClick={() => setActiveFloorPlan(fp)}
                                      data-testid={`open-floor-plan-${fp.id}`}
                                    >
                                      {fp.scale_meters_per_pixel ? t('floorPlan.drawCircuit') : t('floorPlan.calibrate')}
                                    </Button>
                                    <Button
                                      size="sm" variant="ghost"
                                      className="h-6 w-6 p-0 text-red-500 hover:text-red-600"
                                      onClick={() => handleDeleteFloorPlan(fp)}
                                      data-testid={`delete-floor-plan-${fp.id}`}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Spots Needing Location */}
              {isMapExpanded && spots.filter(spot => !spot.lat || !spot.lng).length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3">
                  <h4 className="font-semibold text-orange-800 mb-2 flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4" />
                    Spots Needing Location ({spots.filter(spot => !spot.lat || !spot.lng).length})
                  </h4>
                  <p className="text-xs text-orange-700 mb-2">
                    Click "Set Location" then click on the map to position these spots.
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    {spots
                      .filter(spot => !spot.lat || !spot.lng)
                      .map((spot) => (
                      <div
                        key={spot.id}
                        className="flex items-center justify-between p-2 bg-white rounded border hover:shadow-sm transition-shadow"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 text-xs truncate">{spot.name}</div>
                          <div className="text-xs text-gray-600">
                            {spot.modalities.length > 0 && (
                              <span className="text-emerald-600 text-xs truncate">
                                {spot.modalities.join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className={`text-xs h-7 ml-2 ${dragMode && draggedSpot?.id === spot.id ? 'bg-blue-100 border-blue-300' : ''}`}
                          onClick={() => {
                            if (dragMode && draggedSpot?.id === spot.id) {
                              setDragMode(false);
                              setDraggedSpot(null);
                              toast.info('Location mode cancelled');
                            } else {
                              setDragMode(true);
                              setDraggedSpot(spot);
                              toast.info(`Click map to position: ${spot.name}`);
                            }
                          }}
                        >
                          {dragMode && draggedSpot?.id === spot.id ? 'Cancel' : 'Set Location'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Full Width Map */}
              <div className="relative">
                {/* Map */}
                <div className="w-full relative">
                  {/* Floating Map Controls */}
                  <div className="absolute top-4 left-4 z-50 space-y-2">
                    {/* Address Search */}
                    <div className="flex gap-2">
                      <Autocomplete
                        onLoad={(autocomplete) => setSearchAutocomplete(autocomplete)}
                        onPlaceChanged={() => {
                          if (searchAutocomplete) {
                            const place = searchAutocomplete.getPlace();
                            if (place.geometry?.location) {
                              const lat = place.geometry.location.lat();
                              const lng = place.geometry.location.lng();
                              const address = place.formatted_address || place.name || '';

                              // Pan map to the location
                              if (mapRef.current) {
                                mapRef.current.panTo({ lat, lng });
                                mapRef.current.setZoom(16);
                              }

                              // Store the searched place for spot creation
                              setSearchedPlace({ lat, lng, address, name: place.name || '' });
                              toast.success(`Found: ${address}`);
                            }
                          }
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Search address to create spot..."
                          className="w-72 px-3 py-2 rounded-lg shadow-lg border-0 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </Autocomplete>
                      {searchedPlace && (
                        <button
                          onClick={async () => {
                            try {
                              const newSpot = {
                                event_id: eventId,
                                name: searchedPlace.name || `Spot at ${searchedPlace.address.split(',')[0]}`,
                                description: '',
                                address: searchedPlace.address,
                                lat: searchedPlace.lat,
                                lng: searchedPlace.lng,
                                coverage_role: 'Wide',
                                priority: 'medium',
                                modalities: [],
                                required_gear: [],
                                assigned_photographers: [],
                                max_photographers: 3
                              };
                              await axios.post(`${API}/spots`, newSpot, {
                                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                              });
                              toast.success(`Spot created at ${searchedPlace.address.split(',')[0]}`);
                              setSearchedPlace(null);
                              fetchEventData();
                            } catch (error) {
                              toast.error('Failed to create spot');
                            }
                          }}
                          className="px-3 py-2 bg-emerald-500 text-white rounded-lg shadow-lg text-sm font-medium hover:bg-emerald-600 transition-colors whitespace-nowrap"
                        >
                          + Create Spot Here
                        </button>
                      )}
                    </div>

                    <button
                      onClick={() => {
                        if (mapMode === 'create_spot') {
                          setMapMode('normal');
                          toast.info('Spot creation mode disabled');
                        } else {
                          setMapMode('create_spot');
                          toast.success('Click on map to create a spot');
                        }
                      }}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow-lg font-medium transition-all ${
                        mapMode === 'create_spot'
                          ? 'bg-emerald-500 text-white'
                          : 'bg-white text-gray-700 hover:bg-emerald-50'
                      }`}
                    >
                      <Plus className="w-5 h-5" />
                      {mapMode === 'create_spot' ? 'Creating Spot...' : 'Create Spot'}
                    </button>
                    
                    {/* Map Toggles */}
                    <div className="bg-white rounded-lg shadow-lg p-3 space-y-2">
                      <p className="font-semibold text-gray-700 text-xs mb-2">Map Layers</p>
                      
                      {/* Spots Toggle */}
                      <button
                        onClick={() => setShowSpots(!showSpots)}
                        className={`flex items-center justify-between w-full px-2 py-1 rounded text-xs ${
                          showSpots ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        <span>📍 Spots ({spots.length})</span>
                        {showSpots ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                      </button>
                      
                      {/* Spot Labels Toggle */}
                      {showSpots && (
                        <button
                          onClick={() => setShowSpotLabels(!showSpotLabels)}
                          className={`flex items-center justify-between w-full px-2 py-1 rounded text-xs ml-4 ${
                            showSpotLabels ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-50 text-gray-500'
                          }`}
                        >
                          <span>🏷️ Spot Labels</span>
                          {showSpotLabels ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                        </button>
                      )}
                      
                      {/* Photographers Toggle */}
                      <button
                        onClick={() => setShowPhotographers(!showPhotographers)}
                        className={`flex items-center justify-between w-full px-2 py-1 rounded text-xs ${
                          showPhotographers ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        <span>📷 Photographers ({eventPhotographers.length})</span>
                        {showPhotographers ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                      </button>
                    </div>

                    {spots.length > 0 && showSpots && (
                      <div className="bg-white rounded-lg shadow-lg p-3 text-sm">
                        <p className="font-semibold text-gray-700 mb-1">📍 Spots Legend</p>
                        <p className="text-xs text-gray-500">
                          🟠 Unassigned • 🔵 Assigned
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Click markers for details
                        </p>
                      </div>
                    )}
                    
                    {mapMode === 'create_spot' && (
                      <div className="bg-emerald-50 border-2 border-emerald-400 rounded-lg p-3 animate-pulse max-w-xs">
                        <p className="text-sm font-bold text-emerald-900 mb-1">
                          📍 Spot Creation Mode Active
                        </p>
                        <p className="text-xs text-emerald-700">
                          Click anywhere on the map to place your photography spot
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div 
                    className="relative"
                    onDragOver={(e) => {
                      e.preventDefault(); // Allow drop
                    }}
                    onDrop={async (e) => {
                        e.preventDefault();
                        const photographerData = e.dataTransfer.getData('photographer');
                        if (photographerData) {
                          const photographer = JSON.parse(photographerData);
                          
                          // Get the map container and calculate drop position
                          const mapContainer = e.currentTarget.querySelector('div[role="region"]');
                          if (mapContainer && window.mapInstance) {
                            const rect = mapContainer.getBoundingClientRect();
                            const x = e.clientX - rect.left;
                            const y = e.clientY - rect.top;
                            
                            // Get the bounds and convert pixel to lat/lng
                            const bounds = window.mapInstance.getBounds();
                            const ne = bounds.getNorthEast();
                            const sw = bounds.getSouthWest();
                            
                            // Calculate lat/lng from pixel position
                            const lat = ne.lat() - (y / rect.height) * (ne.lat() - sw.lat());
                            const lng = sw.lng() + (x / rect.width) * (ne.lng() - sw.lng());
                            
                            console.log(`📍 Drop position: ${lat}, ${lng}`);
                            
                            // Calculate distance to each spot using pixel distance (zoom-independent)
                            let nearestSpot = null;
                            let minPixelDistance = Infinity;
                            const PIXEL_THRESHOLD = 60;

                            const projection = window.mapInstance.getProjection();
                            const currentZoom = window.mapInstance.getZoom();
                            const scale = Math.pow(2, currentZoom);

                            spots.filter(s => s.lat && s.lng).forEach(spot => {
                              let pixelDistance;

                              if (projection) {
                                // Pixel-based distance (accurate, zoom-independent)
                                const dropPoint = projection.fromLatLngToPoint(new window.google.maps.LatLng(lat, lng));
                                const spotPoint = projection.fromLatLngToPoint(new window.google.maps.LatLng(spot.lat, spot.lng));
                                pixelDistance = Math.sqrt(
                                  Math.pow((dropPoint.x - spotPoint.x) * scale, 2) +
                                  Math.pow((dropPoint.y - spotPoint.y) * scale, 2)
                                );
                              } else {
                                // Fallback: degree-based with generous threshold
                                const degDistance = Math.sqrt(
                                  Math.pow(lat - spot.lat, 2) +
                                  Math.pow(lng - spot.lng, 2)
                                );
                                // Convert to approximate pixels (rough but workable)
                                pixelDistance = degDistance < 0.005 ? 0 : PIXEL_THRESHOLD + 1;
                              }

                              console.log(`Distance to ${spot.name}: ${pixelDistance.toFixed(1)}px`);

                              if (pixelDistance < minPixelDistance) {
                                minPixelDistance = pixelDistance;
                                nearestSpot = spot;
                              }
                            });

                            console.log(`Nearest spot: ${nearestSpot?.name}, distance: ${minPixelDistance.toFixed(1)}px, threshold: ${PIXEL_THRESHOLD}px`);

                            if (nearestSpot && minPixelDistance < PIXEL_THRESHOLD) {
                              console.log(`✅ Assigning to nearest spot: ${nearestSpot.name}`);
                              try {
                                await axios.post(
                                  `${API}/spots/${nearestSpot.id}/assign`,
                                  { 
                                    spot_id: nearestSpot.id,
                                    photographer_id: photographer.id 
                                  },
                                  { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                                );
                                toast.success(`✅ ${photographer.name} assigned to ${nearestSpot.name}`);
                                setDraggedPhotographerData(null);
                                fetchEventData();
                                return;
                              } catch (error) {
                                console.error('Error assigning photographer:', error);
                                const errorMessage = error.response?.data?.detail 
                                  ? (typeof error.response.data.detail === 'string' 
                                      ? error.response.data.detail 
                                      : JSON.stringify(error.response.data.detail))
                                  : 'Failed to assign photographer';
                                toast.error(errorMessage);
                                setDraggedPhotographerData(null);
                                return;
                              }
                            }
                          }
                          
                          // If not near any spot, allow marker click or spot list drop as fallback
                          setDraggedPhotographerData(photographer);
                          toast.warning(`No spot found near drop location. Drop onto a spot name in the sidebar, or click a map marker to assign ${photographer.name}.`);
                        }
                      }}
                    >
                      <GoogleMap
                        key="event-map"
                        mapContainerStyle={getMapContainerStyle(isMapExpanded)}
                        center={
                          mapCenter || 
                          userLocation || 
                          defaultCenter
                        }
                        zoom={zoom}
                        options={{
                          ...mapOptions,
                          gestureHandling: 'greedy'
                        }}
                        onClick={handleMapClick}
                        onLoad={(map) => {
                          handleMapLoad(map);
                          window.mapInstance = map; // Store for navigation
                          
                          // Set initial center only once - prioritize user location
                          if (!mapInitialized) {
                            // Prefer the first geographic (non-floor-plan) course track.
                            const geoTrack = courseTracks.find(
                              (t) => !t.is_floor_plan
                                && Array.isArray(t.track_points)
                                && t.track_points.length > 0
                                && Number.isFinite(t.track_points[0]?.lat)
                                && Number.isFinite(t.track_points[0]?.lng)
                            );
                            const initialCenter = userLocation
                              ? userLocation
                              : geoTrack
                              ? { lat: geoTrack.track_points[0].lat, lng: geoTrack.track_points[0].lng }
                              : locations.length > 0 && Number.isFinite(locations[0]?.lat) && Number.isFinite(locations[0]?.lng)
                              ? { lat: locations[0].lat, lng: locations[0].lng }
                              : defaultCenter;
                            setMapCenter(initialCenter);
                            setMapInitialized(true);
                            console.log('🗺️ Initial map center set to:', initialCenter);
                          }
                        }}
                        onDragEnd={() => {
                          // Update center when user drags the map
                          if (window.mapInstance && mapInitialized) {
                            const newCenter = window.mapInstance.getCenter();
                            if (newCenter) {
                              setMapCenter({ lat: newCenter.lat(), lng: newCenter.lng() });
                            }
                          }
                        }}
                      >
                      {/* Draw course tracks with directional arrows - only if visible.
                          Skip floor-plan tracks (their track_points are pixel x/y, not lat/lng). */}
                      {courseTracks
                        .filter(track => visibleTracks[track.id] === true && !track.is_floor_plan
                          && Array.isArray(track.track_points)
                          && track.track_points.length > 0
                          && Number.isFinite(track.track_points[0]?.lat))
                        .map((track) => (
                          <Polyline
                            key={track.id}
                            path={track.track_points}
                            options={{
                              strokeColor: track.color,
                              strokeOpacity: 0.8,
                              strokeWeight: 4,
                              clickable: false,
                              icons: [
                                {
                                  icon: {
                                    path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW || 1,
                                    scale: 3,
                                    strokeColor: track.color,
                                    strokeWeight: 2,
                                    fillColor: track.color,
                                    fillOpacity: 1,
                                  },
                                  offset: '0',
                                  repeat: '100px'
                                }
                              ]
                            }}
                          />
                        ))
                      }

                      {/* Photographer pins */}
                      {photographerPins.map((pin) => {
                        console.log('Rendering pin:', pin.id, pin.photographer.name);
                        return (
                          <Marker
                            key={pin.id}
                            position={{ lat: pin.lat, lng: pin.lng }}
                            title={pin.photographer.name}
                            label={{
                              text: '📷',
                              color: 'white',
                              fontSize: '20px'
                            }}
                            icon={{
                              path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                              scale: 20,
                              fillColor: '#10b981',
                              fillOpacity: 1,
                              strokeColor: '#ffffff',
                              strokeWeight: 3
                            }}
                            onClick={() => {
                              console.log('Pin clicked:', pin.id);
                              setSelectedPin(pin);
                            }}
                          />
                        );
                      })}

                      {/* Info window for selected photographer pin */}
                      {selectedPin && (
                        <InfoWindow
                          position={{ lat: selectedPin.lat, lng: selectedPin.lng }}
                          onCloseClick={() => setSelectedPin(null)}
                        >
                          <div className="p-2" style={{ minWidth: '200px' }}>
                            <h3 className="font-bold text-base mb-2">{selectedPin.photographer.name}</h3>
                            <div className="text-sm space-y-1">
                              <p className="text-gray-600">📧 {selectedPin.photographer.email}</p>
                              {selectedPin.photographer.phone && (
                                <p className="text-gray-600">📱 {selectedPin.photographer.phone}</p>
                              )}
                              <p className="text-gray-600">⏰ Max {selectedPin.photographer.max_daily_hours}h/day</p>
                              {selectedPin.photographer.personal_gear && selectedPin.photographer.personal_gear.length > 0 && (
                                <div className="mt-2 pt-2 border-t">
                                  <p className="font-semibold text-xs mb-1">Personal Gear:</p>
                                  <div className="space-y-1 max-h-32 overflow-y-auto">
                                    {selectedPin.photographer.personal_gear.map((gear, idx) => (
                                      <div key={idx} className="text-xs bg-gray-50 p-1 rounded">
                                        <span className="font-medium">{gear.name}</span>
                                        <span className="text-gray-500 ml-1">({gear.type})</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <div className="mt-2 pt-2 border-t">
                                <p className="text-xs text-gray-500">
                                  Position: {selectedPin.lat.toFixed(5)}, {selectedPin.lng.toFixed(5)}
                                </p>
                                <button
                                  onClick={() => {
                                    removePhotographerPin(selectedPin.id);
                                    setSelectedPin(null);
                                  }}
                                  className="mt-2 w-full bg-red-500 hover:bg-red-600 text-white text-xs py-1 px-2 rounded"
                                >
                                  Remove Position
                                </button>
                              </div>
                            </div>
                          </div>
                        </InfoWindow>
                      )}

                      {/* Photography Spot markers with detailed info windows */}
                      {/* Searched place marker */}
                      {searchedPlace && (
                        <Marker
                          position={{ lat: searchedPlace.lat, lng: searchedPlace.lng }}
                          icon={{
                            path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                            scale: 14,
                            fillColor: '#10b981',
                            fillOpacity: 0.9,
                            strokeColor: '#ffffff',
                            strokeWeight: 3
                          }}
                          label={{ text: '+', color: '#ffffff', fontSize: '16px', fontWeight: 'bold' }}
                          title={`Create spot: ${searchedPlace.address}`}
                          onClick={async () => {
                            try {
                              const newSpot = {
                                event_id: eventId,
                                name: searchedPlace.name || `Spot at ${searchedPlace.address.split(',')[0]}`,
                                description: '',
                                address: searchedPlace.address,
                                lat: searchedPlace.lat,
                                lng: searchedPlace.lng,
                                coverage_role: 'Wide',
                                priority: 'medium',
                                modalities: [],
                                required_gear: [],
                                assigned_photographers: [],
                                max_photographers: 3
                              };
                              await axios.post(`${API}/spots`, newSpot, {
                                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                              });
                              toast.success(`Spot created at ${searchedPlace.address.split(',')[0]}`);
                              setSearchedPlace(null);
                              fetchEventData();
                            } catch (error) {
                              toast.error('Failed to create spot');
                            }
                          }}
                        />
                      )}

                      {showSpots && spots
                        .filter(spot => spot.lat && spot.lng) // Only show spots with valid coordinates
                        .map((spot) => {
                          // Get photographer names for this spot
                          const assignedPhotographerNames = spot.assigned_photographers
                            ?.map(photogId => {
                              const photog = eventPhotographers.find(p => p.id === photogId);
                              return photog ? photog.name : null;
                            })
                            .filter(Boolean) || [];
                          
                          return (
                            <React.Fragment key={spot.id}>
                              <Marker
                                position={{ lat: spot.lat, lng: spot.lng }}
                                title={spot.name}
                                label={{
                                  text: '📍',
                                  color: 'white',
                                  fontSize: '18px'
                                }}
                                icon={{
                                  path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                                  scale: 18,
                                  fillColor: spot.assigned_photographers && spot.assigned_photographers.length > 0 ? '#3b82f6' : '#f59e0b',
                                  fillOpacity: 0.9,
                                  strokeColor: selectedSpot?.id === spot.id ? '#000000' : '#ffffff',
                                  strokeWeight: selectedSpot?.id === spot.id ? 3 : 2
                                }}
                                onClick={async () => {
                                  // If photographer is being dragged, assign to this spot
                                  if (draggedPhotographerData) {
                                    try {
                                      await axios.post(
                                        `${API}/spots/${spot.id}/assign`,
                                        { 
                                          spot_id: spot.id,
                                          photographer_id: draggedPhotographerData.id 
                                        },
                                        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                                      );
                                      toast.success(`✅ ${draggedPhotographerData.name} assigned to ${spot.name}`);
                                      setDraggedPhotographerData(null);
                                      fetchEventData();
                                    } catch (error) {
                                      console.error('Error assigning photographer:', error);
                                      // Handle Pydantic validation errors (which are objects)
                                      const errorMessage = error.response?.data?.detail 
                                        ? (typeof error.response.data.detail === 'string' 
                                            ? error.response.data.detail 
                                            : JSON.stringify(error.response.data.detail))
                                        : 'Failed to assign photographer';
                                      toast.error(errorMessage);
                                      setDraggedPhotographerData(null);
                                    }
                                  } else {
                                    // Normal click - show spot details
                                    handleSpotClick(spot);
                                  }
                                }}
                              />
                              
                              {/* Custom label overlay next to the marker - only show when toggle is on */}
                              {showSpotLabels && (
                                <OverlayView
                                  position={{ lat: spot.lat, lng: spot.lng }}
                                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                >
                                  <div
                                    style={{
                                      background: 'white',
                                      border: '2px solid ' + (spot.assigned_photographers && spot.assigned_photographers.length > 0 ? '#3b82f6' : '#f59e0b'),
                                      borderRadius: '8px',
                                      padding: '6px 10px',
                                      fontSize: '11px',
                                      fontWeight: '600',
                                      fontFamily: 'system-ui, -apple-system, sans-serif',
                                      boxShadow: '0 3px 8px rgba(0,0,0,0.25)',
                                      transform: 'translate(28px, -15px)',
                                      pointerEvents: isDraggingPhotographer ? 'auto' : 'none',
                                      minWidth: '120px',
                                      maxWidth: '280px',
                                      transition: 'border-color 0.15s, background 0.15s'
                                    }}
                                    onDragOver={isDraggingPhotographer ? (e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      e.currentTarget.style.borderColor = '#2563eb';
                                      e.currentTarget.style.background = '#eff6ff';
                                    } : undefined}
                                    onDragLeave={isDraggingPhotographer ? (e) => {
                                      e.currentTarget.style.borderColor = spot.assigned_photographers?.length > 0 ? '#3b82f6' : '#f59e0b';
                                      e.currentTarget.style.background = 'white';
                                    } : undefined}
                                    onDrop={isDraggingPhotographer ? async (e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const photographerData = e.dataTransfer.getData('photographer');
                                      if (photographerData) {
                                        const photographer = JSON.parse(photographerData);
                                        try {
                                          await axios.post(
                                            `${API}/spots/${spot.id}/assign`,
                                            { spot_id: spot.id, photographer_id: photographer.id },
                                            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                                          );
                                          toast.success(`${photographer.name} assigned to ${spot.name}`);
                                          setDraggedPhotographerData(null);
                                          setIsDraggingPhotographer(false);
                                          fetchEventData();
                                        } catch (error) {
                                          const msg = error.response?.data?.detail || 'Failed to assign';
                                          toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
                                        }
                                      }
                                    } : undefined}
                                  >
                                    <div style={{ 
                                      color: '#111827', 
                                      marginBottom: assignedPhotographerNames.length > 0 ? '3px' : '0',
                                      letterSpacing: '0.01em',
                                      lineHeight: '1.3',
                                      wordBreak: 'break-word'
                                    }}>
                                      {spot.name}
                                    </div>
                                    {assignedPhotographerNames.length > 0 && (
                                      <div style={{ 
                                        color: '#3b82f6', 
                                        fontSize: '10px',
                                        fontWeight: '500',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '4px',
                                        lineHeight: '1.3'
                                      }}>
                                        <span style={{ flexShrink: 0 }}>👤</span>
                                        <span style={{ 
                                          wordBreak: 'break-word',
                                          flex: 1
                                        }}>
                                          {assignedPhotographerNames.join(', ')}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </OverlayView>
                              )}
                            </React.Fragment>
                          );
                        })}
                      
                      
                      {/* Photographer markers */}
                      {showPhotographers && eventPhotographers
                        .filter(photographer => photographer.current_location?.lat && photographer.current_location?.lng)
                        .map((photographer) => (
                        <Marker
                          key={photographer.id}
                          position={{ 
                            lat: photographer.current_location.lat, 
                            lng: photographer.current_location.lng 
                          }}
                          title={photographer.name}
                          icon={{
                            path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                            scale: 12,
                            fillColor: '#2563eb',
                            fillOpacity: 1.0,
                            strokeColor: '#ffffff',
                            strokeWeight: 2
                          }}
                          label={{
                            text: '📷',
                            color: 'white',
                            fontSize: '12px'
                          }}
                        />
                      ))}
                      
                      
                      {/* Info window for selected spot */}
                      {selectedSpot && (
                        <InfoWindow
                          position={{ lat: selectedSpot.lat, lng: selectedSpot.lng }}
                          onCloseClick={() => setSelectedSpot(null)}
                        >
                          <div className="p-3" style={{ minWidth: '280px', maxWidth: '350px' }}>
                            <div className="flex items-start justify-between mb-2">
                              <h3 className="font-bold text-base">{selectedSpot.name}</h3>
                              <span className={`text-xs px-2 py-1 rounded ${
                                selectedSpot.assigned_photographers?.length > 0 ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                              }`}>
                                {selectedSpot.coverage_role}
                              </span>
                            </div>
                            
                            {selectedSpot.description && (
                              <p className="text-sm text-gray-600 mb-2">{selectedSpot.description}</p>
                            )}
                            
                            {selectedSpot.address && (
                              <p className="text-sm text-gray-600 mb-2">📍 {selectedSpot.address}</p>
                            )}
                            
                            <div className="space-y-2 text-xs">
                              {/* Modalities */}
                              {selectedSpot.modalities && selectedSpot.modalities.length > 0 && (
                                <div className="bg-blue-50 p-2 rounded">
                                  <p className="font-semibold mb-1">🏃 Activity Stations</p>
                                  <div className="flex flex-wrap gap-1">
                                    {selectedSpot.modalities.map((modality, idx) => (
                                      <span key={idx} className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">
                                        {modality}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Timing Info */}
                              {(selectedSpot.arrive_time || selectedSpot.leave_time) && (
                                <div className="bg-gray-50 p-2 rounded">
                                  <p className="font-semibold mb-1">⏰ Timing</p>
                                  {selectedSpot.arrive_time && <p>Arrive: {selectedSpot.arrive_time}</p>}
                                  {selectedSpot.leave_time && <p>Leave: {selectedSpot.leave_time}</p>}
                                  {selectedSpot.first_athlete_time && <p>First: {selectedSpot.first_athlete_time}</p>}
                                  {selectedSpot.last_athlete_time && <p>Last: {selectedSpot.last_athlete_time}</p>}
                                </div>
                              )}
                              
                              {/* Gear Requirements */}
                              {selectedSpot.required_gear && selectedSpot.required_gear.length > 0 && (
                                <div className="bg-gray-50 p-2 rounded">
                                  <p className="font-semibold mb-1">📷 Required Gear</p>
                                  <div className="flex flex-wrap gap-1">
                                    {selectedSpot.required_gear.map((gear, idx) => (
                                      <span key={idx} className="bg-white px-2 py-0.5 rounded border text-xs">
                                        {gear}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {selectedSpot.has_flash && (
                                <div className="bg-yellow-50 p-2 rounded">
                                  <p>⚡ Flash Required</p>
                                </div>
                              )}
                              
                              {/* Assigned Photographers */}
                              <div className="bg-emerald-50 p-2 rounded">
                                <p className="font-semibold mb-1">
                                  👥 Photographers ({selectedSpot.assigned_photographers?.length || 0}/{selectedSpot.max_photographers})
                                </p>
                                {selectedSpot.assigned_photographers && selectedSpot.assigned_photographers.length > 0 ? (
                                  <div className="space-y-1">
                                    {selectedSpot.assigned_photographers.map(photoId => {
                                      const photographer = photographers.find(p => p.id === photoId);
                                      return photographer ? (
                                        <div key={photoId} className="bg-white px-2 py-1 rounded text-xs flex justify-between items-center">
                                          <span>{photographer.name}</span>
                                          <button
                                            onClick={async () => {
                                              try {
                                                await axios.delete(
                                                  `${API}/spots/${selectedSpot.id}/unassign/${photoId}`,
                                                  { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                                                );
                                                toast.success(`Removed ${photographer.name} from ${selectedSpot.name}`);
                                                setSelectedSpot(null);
                                                fetchEventData();
                                              } catch (error) {
                                                console.error('Error unassigning photographer:', error);
                                                toast.error('Failed to remove photographer');
                                              }
                                            }}
                                            className="text-red-500 hover:text-red-700 ml-2"
                                            title="Remove photographer"
                                          >
                                            ✕
                                          </button>
                                        </div>
                                      ) : null;
                                    })}
                                  </div>
                                ) : (
                                  <p className="text-gray-500 italic text-xs">No photographers assigned</p>
                                )}
                              </div>
                              
                              {/* Action Buttons */}
                              <div className="space-y-2 pt-2">
                                <button
                                  onClick={() => {
                                    // Open edit dialog with full spot data
                                    setEditingSpot({...selectedSpot});
                                    setShowEditDialog(true);
                                    setSelectedSpot(null); // Close the InfoWindow
                                  }}
                                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs py-2 px-3 rounded font-medium"
                                >
                                  ✏️ Edit Spot
                                </button>
                                <button
                                  onClick={() => {
                                    // Enable move mode for this spot
                                    setDragMode(true);
                                    setDraggedSpot(selectedSpot);
                                    setSelectedSpot(null); // Close the InfoWindow
                                    toast.info(`Click on the map to move "${selectedSpot.name}" to new location`);
                                  }}
                                  className="w-full bg-purple-500 hover:bg-purple-600 text-white text-xs py-2 px-3 rounded font-medium"
                                >
                                  📍 Move Location
                                </button>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      // Get directions
                                      const url = `https://www.google.com/maps/dir/?api=1&destination=${selectedSpot.lat},${selectedSpot.lng}`;
                                      window.open(url, '_blank');
                                    }}
                                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white text-xs py-1.5 px-3 rounded"
                                  >
                                    🗺️ Directions
                                  </button>
                                  <button
                                    onClick={async () => {
                                      // Duplicate spot - create a copy and let user position it
                                      try {
                                        const duplicatedSpot = {
                                          ...selectedSpot,
                                          id: undefined, // Will be generated by backend
                                          name: `${selectedSpot.name} (Copy)`,
                                          lat: null, // User will set location
                                          lng: null,
                                          assigned_photographers: [] // Clear assignments
                                        };
                                        
                                        // Create the duplicated spot without location
                                        const response = await axios.post(
                                          `${API}/spots`,
                                          duplicatedSpot,
                                          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                                        );
                                        
                                        const newSpot = response.data;
                                        toast.success(`"${newSpot.name}" created! Click on map to place it.`);
                                        
                                        // Enter drag mode to position the new spot
                                        setDragMode(true);
                                        setDraggedSpot(newSpot);
                                        setSelectedSpot(null); // Close the InfoWindow
                                        
                                        // Refresh to show in "Spots Needing Location"
                                        fetchEventData();
                                      } catch (error) {
                                        console.error('Duplicate spot error:', error);
                                        toast.error('Failed to duplicate spot');
                                      }
                                    }}
                                    className="flex-1 bg-indigo-500 hover:bg-indigo-600 text-white text-xs py-1.5 px-3 rounded"
                                  >
                                    📋 Duplicate
                                  </button>
                                </div>
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`Are you sure you want to delete "${selectedSpot.name}"? This action cannot be undone.`)) {
                                      return;
                                    }
                                    
                                    try {
                                      await axios.delete(
                                        `${API}/spots/${selectedSpot.id}`,
                                        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                                      );
                                      toast.success(`"${selectedSpot.name}" deleted successfully`);
                                      setSelectedSpot(null); // Close the InfoWindow
                                      fetchEventData(); // Refresh the data
                                    } catch (error) {
                                      console.error('Delete spot error:', error);
                                      toast.error('Failed to delete spot');
                                    }
                                  }}
                                  className="w-full bg-red-500 hover:bg-red-600 text-white text-xs py-2 px-3 rounded font-medium"
                                >
                                  🗑️ Remove Spot
                                </button>
                              </div>
                              
                              <p className="text-gray-400 text-xs pt-1">
                                📍 {selectedSpot.lat.toFixed(5)}, {selectedSpot.lng.toFixed(5)}
                              </p>
                            </div>
                          </div>
                        </InfoWindow>
                      )}
                      
                      {/* Floor Plan Overlay */}
                      {floorPlanImage && floorPlanBounds && (
                        <GroundOverlay
                          url={floorPlanImage}
                          bounds={floorPlanBounds}
                          opacity={0.7}
                        />
                      )}

                      {/* Location markers */}
                      {locations.map((location) => (
                        <Marker
                          key={location.id}
                          position={{ lat: location.lat, lng: location.lng }}
                          title={location.name}
                          onClick={() => {
                            setSelectedLocation(location);
                            if (selectedPhotographer) {
                              setOpenAssignment(true);
                            }
                          }}
                        />
                      ))}
                    </GoogleMap>
                  </div>
                </div>
              </div>


              {/* Assignment Dialog */}
              <Dialog open={openAssignment} onOpenChange={setOpenAssignment}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Assign Photographer to Location</DialogTitle>
                  </DialogHeader>
                  {selectedLocation && selectedPhotographer && (
                    <div className="space-y-4">
                      <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                        <div>
                          <p className="text-sm text-slate-600">Location</p>
                          <p className="font-semibold">{selectedLocation.name}</p>
                          <p className="text-sm text-slate-600">{selectedLocation.address}</p>
                        </div>
                        <div className="pt-2 border-t border-slate-200">
                          <p className="text-sm text-slate-600">Photographer</p>
                          <p className="font-semibold">{selectedPhotographer.name}</p>
                          {selectedPhotographer.personal_gear && selectedPhotographer.personal_gear.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-slate-600 mb-1">Personal Gear:</p>
                              <div className="flex flex-wrap gap-1">
                                {selectedPhotographer.personal_gear.slice(0, 3).map((gear, idx) => (
                                  <span key={idx} className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">
                                    {gear.name}
                                  </span>
                                ))}
                                {selectedPhotographer.personal_gear.length > 3 && (
                                  <span className="text-xs px-2 py-0.5 bg-slate-200 text-slate-700 rounded">
                                    +{selectedPhotographer.personal_gear.length - 3} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-slate-600">
                        This will create an assignment record for the selected plan. Make sure a plan is created first.
                      </p>
                      <div className="flex gap-3">
                        <Button variant="outline" onClick={() => setOpenAssignment(false)} data-testid="cancel-assignment-btn">
                          Cancel
                        </Button>
                        <Button
                          onClick={() => {
                            toast.info('Assignment feature ready. Create a plan and time slots first.');
                            setOpenAssignment(false);
                          }}
                          className="btn-gradient"
                          data-testid="confirm-assignment-btn"
                        >
                          Confirm Assignment
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
              
              {/* Floor Plan Bounds Dialog */}
              <Dialog open={openFloorPlan} onOpenChange={setOpenFloorPlan}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Set Floor Plan Overlay Bounds</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Set the geographic bounds for your floor plan overlay. You can find these coordinates on the map corners.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>North Latitude</Label>
                        <Input
                          type="number"
                          step="any"
                          value={boundsForm.north}
                          onChange={(e) => setBoundsForm({ ...boundsForm, north: e.target.value })}
                          placeholder="-33.865"
                        />
                      </div>
                      <div>
                        <Label>South Latitude</Label>
                        <Input
                          type="number"
                          step="any"
                          value={boundsForm.south}
                          onChange={(e) => setBoundsForm({ ...boundsForm, south: e.target.value })}
                          placeholder="-33.875"
                        />
                      </div>
                      <div>
                        <Label>East Longitude</Label>
                        <Input
                          type="number"
                          step="any"
                          value={boundsForm.east}
                          onChange={(e) => setBoundsForm({ ...boundsForm, east: e.target.value })}
                          placeholder="151.215"
                        />
                      </div>
                      <div>
                        <Label>West Longitude</Label>
                        <Input
                          type="number"
                          step="any"
                          value={boundsForm.west}
                          onChange={(e) => setBoundsForm({ ...boundsForm, west: e.target.value })}
                          placeholder="151.205"
                        />
                      </div>
                    </div>
                    {floorPlanImage && (
                      <div className="border rounded-lg p-2">
                        <img src={floorPlanImage} alt="Floor plan preview" className="w-full h-32 object-contain" />
                      </div>
                    )}
                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => setOpenFloorPlan(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleSetFloorPlanBounds} className="btn-gradient">
                        Set Overlay
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* New Floor Plan upload dialog (calibrated, indoor) */}
              <Dialog
                open={floorPlanUploadOpen}
                onOpenChange={(o) => {
                  setFloorPlanUploadOpen(o);
                  if (!o) { setFloorPlanUploadFile(null); setFloorPlanUploadName(''); }
                }}
              >
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t('floorPlan.upload')}</DialogTitle>
                    <DialogDescription>{t('floorPlan.uploadHint')}</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>{t('floorPlan.uploadName')}</Label>
                      <Input
                        value={floorPlanUploadName}
                        onChange={(e) => setFloorPlanUploadName(e.target.value)}
                        placeholder={t('floorPlan.uploadNamePlaceholder')}
                        data-testid="floor-plan-name-input"
                      />
                    </div>
                    <div>
                      <Label>Image</Label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFloorPlanFileChange}
                        className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                        data-testid="floor-plan-file-input"
                      />
                      {floorPlanUploadFile && (
                        <p className="text-xs text-slate-500 mt-1 truncate">{floorPlanUploadFile.name}</p>
                      )}
                    </div>
                    <div className="flex gap-3 justify-end">
                      <Button variant="outline" onClick={() => setFloorPlanUploadOpen(false)}>
                        {t('floorPlan.cancel')}
                      </Button>
                      <Button
                        onClick={submitFloorPlanUpload}
                        disabled={floorPlanUploading || !floorPlanUploadFile || !floorPlanUploadName.trim()}
                        className="btn-gradient"
                        data-testid="floor-plan-upload-submit"
                      >
                        {floorPlanUploading ? t('floorPlan.uploading') : t('floorPlan.uploadSubmit')}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Quick Spot Creation Dialog */}
              <Dialog open={quickSpotDialog} onOpenChange={setQuickSpotDialog}>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Create Photography Spot</DialogTitle>
                  </DialogHeader>
                  {quickSpotData && (
                    <QuickSpotForm
                      initialData={quickSpotData}
                      photographers={eventPhotographers}
                      courseTimings={courseTimings}
                      eventType={event?.event_type || 'general'}
                      customModalities={event?.custom_modalities || []}
                      onSave={handleQuickCreateSpot}
                      onCancel={() => setQuickSpotDialog(false)}
                    />
                  )}
                </DialogContent>
              </Dialog>
            </Card>
          </TabsContent>

          <TabsContent value="spots" className="flex-1 overflow-y-auto space-y-4">
            <Card className="glass-card p-6">
              <SpotManagement
                eventId={eventId}
                eventType={event?.event_type || 'general'}
                customModalities={event?.custom_modalities || []}
                spots={filteredSpots}
                onSpotsChange={fetchEventData}
                photographers={photographers}
                eventPhotographers={eventPhotographers}
                courseTimings={courseTimings}
                setMapMode={setMapMode}
                onSpotSelect={handleSpotClick}
                editSpotIdRequest={editSpotIdRequest}
                onEditConsumed={() => setEditSpotIdRequest(null)}
              />
            </Card>
          </TabsContent>


          <TabsContent value="assignments" className="h-[calc(100vh-200px)]">
            <AssignmentManager
              eventId={eventId}
              onNavigateToSpot={(spot) => {
                handleTabChange('map');
                setTimeout(() => {
                  if (mapRef.current && spot.lat && spot.lng) {
                    mapRef.current.panTo({ lat: spot.lat, lng: spot.lng });
                    mapRef.current.setZoom(19);
                  }
                }, 300);
              }}
              onEditSpot={(spot) => {
                handleTabChange('spots');
                setEditSpotIdRequest(spot.id);
              }}
            />
          </TabsContent>


          <TabsContent value="timing" className="flex-1 overflow-y-auto space-y-4">
            <Card className="glass-card p-6">
              <CourseTimingUpload 
                eventId={eventId}
                onTimingsChange={fetchEventData}
              />
            </Card>
          </TabsContent>

          <TabsContent value="plans" className="flex-1 overflow-y-auto space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Coverage Plans</h2>
              <Dialog open={openPlan} onOpenChange={setOpenPlan}>
                <DialogTrigger asChild>
                  <Button className="btn-gradient" data-testid="create-plan-btn">
                    <Plus className="w-5 h-5 mr-2" />
                    Create Plan
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Coverage Plan</DialogTitle>
                  </DialogHeader>
                  <div className="py-4">
                    <p className="text-sm text-slate-600 mb-4">
                      A new plan will be created with an auto-generated version number.
                    </p>
                    <p className="text-xs text-slate-500 bg-slate-50 p-3 rounded border border-slate-200">
                      Example: "{event?.name} - v1", "{event?.name} - v2", etc.
                    </p>
                  </div>
                  <div className="flex gap-3 justify-end">
                    <Button type="button" variant="outline" onClick={() => setOpenPlan(false)} data-testid="cancel-plan-btn">
                      Cancel
                    </Button>
                    <Button onClick={handleCreatePlan} className="btn-gradient" data-testid="submit-plan-btn">
                      Create Plan
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            {filteredPlans.length === 0 ? (
              <Card className="glass-card p-12 text-center">
                <Camera className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-600">{plans.length === 0 ? 'No plans created yet' : 'No plans for this day'}</p>
              </Card>
            ) : (
              <div className="grid-3">
                {filteredPlans.map((plan) => (
                  <Card key={plan.id} className="glass-card p-6 hover:shadow-xl transition-all relative group" data-testid={`plan-card-${plan.id}`}>
                    {/* Edit and Delete Buttons - shown on hover */}
                    <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2 z-10">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setEditingPlan(plan);
                          setEditPlanName(plan.name);
                        }}
                        className="bg-white border-blue-500 text-blue-700 hover:bg-blue-50"
                        title="Edit plan name"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeletePlan(plan.id);
                        }}
                        className="bg-white border-red-500 text-red-700 hover:bg-red-50"
                        title="Delete plan"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <Link to={`/events/${eventId}/plan/${plan.id}`} className="block">
                      <div className="flex items-start justify-between mb-3">
                        <Camera className="w-8 h-8 text-emerald-600" />
                        <span className={`status-badge status-${plan.status?.toLowerCase() || 'draft'}`}>
                          {plan.status || 'Draft'}
                        </span>
                      </div>
                      <h3 className="font-semibold text-lg mb-2">{plan.name}</h3>
                      <p className="text-sm text-slate-600">Created {new Date(plan.created_at).toLocaleDateString()}</p>
                    </Link>
                  </Card>
                ))}
              </div>
            )}
            
            {/* Edit Plan Dialog */}
            <Dialog open={!!editingPlan} onOpenChange={(open) => {
              if (!open) {
                setEditingPlan(null);
                setEditPlanName('');
              }
            }}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Plan Name</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleEditPlan} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-plan-name">Plan Name</Label>
                    <Input
                      id="edit-plan-name"
                      value={editPlanName}
                      onChange={(e) => setEditPlanName(e.target.value)}
                      required
                      placeholder="Enter new plan name"
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setEditingPlan(null);
                        setEditPlanName('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="btn-gradient">
                      Save Changes
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="photos" className="flex-1 overflow-y-auto space-y-4">
            <EventPhotosGallery eventId={eventId} canDelete={canEdit(user, 'spots') || isPhotoEditor(user)} />
          </TabsContent>

          <TabsContent value="chat" className="flex-1 overflow-hidden">
            <EventChat eventId={eventId} apiClient={api} selfName={user?.name || user?.email || 'coordinator'} />
          </TabsContent>
        </Tabs>

        {/* Event Photographer Assignment Dialog */}
        <EventPhotographerAssignment
          eventId={eventId}
          open={photographerAssignmentOpen}
          onOpenChange={setPhotographerAssignmentOpen}
          onAssignmentChange={fetchEventData}
        />

        {/* Edit Spot Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Edit Photography Spot</DialogTitle>
              <DialogDescription>
                Update spot details, timing, gear requirements, and reference images
              </DialogDescription>
            </DialogHeader>
            {editingSpot && (
              <div className="space-y-4 py-4">
                {/* Name and Coverage Role */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Spot Name *</label>
                    <input
                      type="text"
                      value={editingSpot.name || ''}
                      onChange={(e) => setEditingSpot({...editingSpot, name: e.target.value})}
                      className="w-full border rounded px-3 py-2 text-sm"
                      placeholder="e.g., Swim Start, Run Finish"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Coverage Role</label>
                    <select
                      value={editingSpot.coverage_role || 'Wide'}
                      onChange={(e) => setEditingSpot({...editingSpot, coverage_role: e.target.value})}
                      className="w-full border rounded px-3 py-2 text-sm"
                    >
                      <option value="Wide">Wide</option>
                      <option value="Tele">Tele</option>
                      <option value="Crowd">Crowd</option>
                      <option value="Drone">Drone</option>
                      <option value="Social">Social</option>
                      <option value="Video">Video</option>
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={editingSpot.description || ''}
                    onChange={(e) => setEditingSpot({...editingSpot, description: e.target.value})}
                    className="w-full border rounded px-3 py-2 text-sm"
                    rows={2}
                    placeholder="Describe the spot, angles, lighting conditions..."
                  />
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium mb-1">📍 Address</label>
                  <input
                    type="text"
                    value={editingSpot.address || ''}
                    onChange={(e) => setEditingSpot({...editingSpot, address: e.target.value})}
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="Street address or location description"
                  />
                </div>

                {/* Scene Types / Modalities */}
                <div>
                  <label className="block text-sm font-medium mb-2">Scene Types / Activities</label>
                  <div className="space-y-3">
                    {Object.entries(getEventModalities()).map(([group, modalities]) => (
                      <div key={group}>
                        <p className="text-xs font-semibold text-gray-500 mb-1">{group}</p>
                        <div className="flex flex-wrap gap-2">
                          {modalities.map(modality => {
                            const isChecked = (editingSpot.modalities || []).includes(modality);
                            return (
                              <button
                                key={modality}
                                type="button"
                                onClick={() => {
                                  const modalities = editingSpot.modalities || [];
                                  if (isChecked) {
                                    setEditingSpot({...editingSpot, modalities: modalities.filter(m => m !== modality)});
                                  } else {
                                    setEditingSpot({...editingSpot, modalities: [...modalities, modality]});
                                  }
                                }}
                                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                  isChecked
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {modality}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Custom modality input */}
                  <div className="mt-3 flex gap-2">
                    <input
                      type="text"
                      placeholder="Add custom scene type..."
                      className="flex-1 border rounded px-3 py-1.5 text-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target.value.trim()) {
                          e.preventDefault();
                          const newMod = e.target.value.trim();
                          const modalities = editingSpot.modalities || [];
                          if (!modalities.includes(newMod)) {
                            setEditingSpot({...editingSpot, modalities: [...modalities, newMod]});
                          }
                          e.target.value = '';
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        const input = e.target.parentElement.querySelector('input');
                        if (input.value.trim()) {
                          const newMod = input.value.trim();
                          const modalities = editingSpot.modalities || [];
                          if (!modalities.includes(newMod)) {
                            setEditingSpot({...editingSpot, modalities: [...modalities, newMod]});
                          }
                          input.value = '';
                        }
                      }}
                      className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-sm font-medium"
                    >
                      + Add
                    </button>
                  </div>
                </div>

                {/* Priority and Flash */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Priority</label>
                    <select
                      value={editingSpot.priority || 'medium'}
                      onChange={(e) => setEditingSpot({...editingSpot, priority: e.target.value})}
                      className="w-full border rounded px-3 py-2 text-sm"
                    >
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Flash Required</label>
                    <select
                      value={editingSpot.flash_required ? 'true' : 'false'}
                      onChange={(e) => setEditingSpot({...editingSpot, flash_required: e.target.value === 'true'})}
                      className="w-full border rounded px-3 py-2 text-sm"
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Max Photographers</label>
                    <input
                      type="number"
                      value={editingSpot.max_photographers || 1}
                      onChange={(e) => setEditingSpot({...editingSpot, max_photographers: parseInt(e.target.value)})}
                      className="w-full border rounded px-3 py-2 text-sm"
                      min="1"
                    />
                  </div>
                </div>

                {/* Timing */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">⏰ Timing Information</h3>
                  
                  {/* Timing Suggestions */}
                  {editingSpot.modalities && editingSpot.modalities.length > 0 && getTimingSuggestions(editingSpot.modalities).length > 0 && (
                    <div className="mb-4 p-3 bg-amber-50 rounded border border-amber-200">
                      <p className="text-xs font-medium text-amber-800 mb-2">💡 Timing Suggestions from Course Data:</p>
                      <div className="space-y-2">
                        {getTimingSuggestions(editingSpot.modalities).map((suggestion, idx) => (
                          <div key={idx} className="bg-white p-2 rounded border text-xs">
                            <p className="font-medium text-gray-900 mb-1">{suggestion.modality}</p>
                            <p className="text-gray-600 mb-2">{suggestion.timeInfo}</p>
                            <button
                              onClick={() => {
                                // Auto-populate times: arrive 1hr before start, leave at cutoff
                                const updates = {};
                                if (suggestion.data.start_time) {
                                  // Calculate 1 hour before start time
                                  const [hours, minutes] = suggestion.data.start_time.split(':');
                                  const startMinutes = parseInt(hours) * 60 + parseInt(minutes);
                                  const arriveMinutes = startMinutes - 60;
                                  const arriveHours = Math.floor(arriveMinutes / 60);
                                  const arriveMins = arriveMinutes % 60;
                                  updates.arrive_time = `${String(arriveHours).padStart(2, '0')}:${String(arriveMins).padStart(2, '0')}`;
                                }
                                if (suggestion.data.cutoff_time) {
                                  updates.leave_time = suggestion.data.cutoff_time;
                                  updates.cutoff_time = suggestion.data.cutoff_time;
                                }
                                if (suggestion.data.first_finisher_time) {
                                  updates.first_athlete_time = suggestion.data.first_finisher_time;
                                }
                                if (suggestion.data.last_finisher_time) {
                                  updates.last_athlete_time = suggestion.data.last_finisher_time;
                                }
                                setEditingSpot({...editingSpot, ...updates});
                                toast.success(`Applied timing from ${suggestion.modality}`);
                              }}
                              className="px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded text-xs"
                            >
                              Apply This Timing
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Arrive Time</label>
                      <input
                        type="time"
                        value={editingSpot.arrive_time || ''}
                        onChange={(e) => setEditingSpot({...editingSpot, arrive_time: e.target.value})}
                        className="w-full border rounded px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Leave Time</label>
                      <input
                        type="time"
                        value={editingSpot.leave_time || ''}
                        onChange={(e) => setEditingSpot({...editingSpot, leave_time: e.target.value})}
                        className="w-full border rounded px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">First Athlete Time</label>
                      <input
                        type="time"
                        value={editingSpot.first_athlete_time || ''}
                        onChange={(e) => setEditingSpot({...editingSpot, first_athlete_time: e.target.value})}
                        className="w-full border rounded px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Last Athlete Time</label>
                      <input
                        type="time"
                        value={editingSpot.last_athlete_time || ''}
                        onChange={(e) => setEditingSpot({...editingSpot, last_athlete_time: e.target.value})}
                        className="w-full border rounded px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Cutoff Time</label>
                      <input
                        type="time"
                        value={editingSpot.cutoff_time || ''}
                        onChange={(e) => setEditingSpot({...editingSpot, cutoff_time: e.target.value})}
                        className="w-full border rounded px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Break Time 1</label>
                      <input
                        type="text"
                        placeholder="e.g., 10:00-10:15"
                        value={editingSpot.break_time_1 || ''}
                        onChange={(e) => setEditingSpot({...editingSpot, break_time_1: e.target.value})}
                        className="w-full border rounded px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Break Time 2</label>
                      <input
                        type="text"
                        placeholder="e.g., 13:00-13:15"
                        value={editingSpot.break_time_2 || ''}
                        onChange={(e) => setEditingSpot({...editingSpot, break_time_2: e.target.value})}
                        className="w-full border rounded px-3 py-2 text-sm"
                      />
                    </div>
                  </div>

                  {/* Per-photographer times */}
                  {editingSpot.assigned_photographers && editingSpot.assigned_photographers.length > 0 && (
                    <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <h4 className="text-sm font-semibold text-purple-900 mb-3 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Individual Photographer Times ({editingSpot.assigned_photographers.length})
                      </h4>
                      <p className="text-xs text-purple-700 mb-3">
                        Each photographer can have different arrive/leave times at this spot. Leave blank to use the spot's default times above.
                      </p>
                      <div className="space-y-3">
                        {editingSpot.assigned_photographers.map(photogId => {
                          const photog = eventPhotographers.find(p => p.id === photogId) || photographers.find(p => p.id === photogId);
                          const pt = editingSpot.photographer_times?.[photogId] || {};
                          return (
                            <div key={photogId} className="bg-white rounded border p-3">
                              <p className="text-sm font-medium text-gray-900 mb-2">{photog?.name || photogId}</p>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Arrive</label>
                                  <input
                                    type="time"
                                    value={pt.arrive_time || ''}
                                    onChange={(e) => {
                                      const newTimes = { ...editingSpot.photographer_times || {} };
                                      newTimes[photogId] = { ...newTimes[photogId] || {}, arrive_time: e.target.value || null };
                                      setEditingSpot({ ...editingSpot, photographer_times: newTimes });
                                    }}
                                    placeholder={editingSpot.arrive_time || ''}
                                    className="w-full border rounded px-3 py-1.5 text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Leave</label>
                                  <input
                                    type="time"
                                    value={pt.leave_time || ''}
                                    onChange={(e) => {
                                      const newTimes = { ...editingSpot.photographer_times || {} };
                                      newTimes[photogId] = { ...newTimes[photogId] || {}, arrive_time: (newTimes[photogId]?.arrive_time || null), leave_time: e.target.value || null };
                                      setEditingSpot({ ...editingSpot, photographer_times: newTimes });
                                    }}
                                    placeholder={editingSpot.leave_time || ''}
                                    className="w-full border rounded px-3 py-1.5 text-sm"
                                  />
                                </div>
                              </div>
                              {!pt.arrive_time && !pt.leave_time && (
                                <p className="text-[10px] text-gray-400 mt-1">Using spot default: {editingSpot.arrive_time || '??'} - {editingSpot.leave_time || '??'}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Break coverage */}
                <BreakCoverageEditor
                  spot={editingSpot}
                  eventPhotographers={eventPhotographers}
                  onChange={(updated) => setEditingSpot(updated)}
                  onPersist={fetchEventData}
                />

                {/* High-res photos uploaded by photographers from the share link */}
                <SpotPhotosGallery eventId={eventId} spotId={editingSpot.id} />

                {/* Lighting Options */}
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium mb-2">💡 Lighting Options</label>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingSpot.has_flash || false}
                        onChange={(e) => setEditingSpot({...editingSpot, has_flash: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Flash Required</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingSpot.use_led_flash || false}
                        onChange={(e) => setEditingSpot({...editingSpot, use_led_flash: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Use LED/Continuous Light (instead of traditional flash)</span>
                    </label>
                  </div>
                </div>

                {/* Required Gear */}
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium mb-2">📷 Required Gear</label>
                  
                  {/* Suggested Gear */}
                  {editingSpot.modalities && editingSpot.modalities.length > 0 && (
                    <div className="mb-3 p-3 bg-blue-50 rounded border border-blue-200">
                      <p className="text-xs font-medium text-blue-800 mb-2">✨ Suggested Gear for Selected Modalities:</p>
                      <div className="flex flex-wrap gap-2">
                        {getSuggestedGear(editingSpot.modalities).map((gear, idx) => (
                          <button
                            key={idx}
                            onClick={() => {
                              if (!(editingSpot.required_gear || []).includes(gear)) {
                                setEditingSpot({...editingSpot, required_gear: [...(editingSpot.required_gear || []), gear]});
                                toast.success(`Added ${gear}`);
                              }
                            }}
                            className="bg-white border border-blue-300 px-2 py-1 rounded text-xs hover:bg-blue-100 transition-colors"
                          >
                            + {gear}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(editingSpot.required_gear || []).map((gear, idx) => (
                      <span key={idx} className="bg-emerald-100 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                        {gear}
                        <button
                          onClick={() => {
                            const newGear = [...(editingSpot.required_gear || [])];
                            newGear.splice(idx, 1);
                            setEditingSpot({...editingSpot, required_gear: newGear});
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Add custom gear (e.g., 85mm f/1.4)"
                      className="flex-1 border rounded px-3 py-2 text-sm"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && e.target.value.trim()) {
                          const newGear = [...(editingSpot.required_gear || []), e.target.value.trim()];
                          setEditingSpot({...editingSpot, required_gear: newGear});
                          e.target.value = '';
                        }
                      }}
                    />
                    <button
                      className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm"
                      onClick={(e) => {
                        const input = e.target.parentElement.querySelector('input');
                        if (input.value.trim()) {
                          const newGear = [...(editingSpot.required_gear || []), input.value.trim()];
                          setEditingSpot({...editingSpot, required_gear: newGear});
                          input.value = '';
                        }
                      }}
                    >
                      Add
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Press Enter or click Add to add custom gear</p>
                </div>

                {/* Reference Images */}
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium mb-2">🖼️ Reference Images</label>
                  
                  {/* Existing Images */}
                  {editingSpot.reference_images && editingSpot.reference_images.length > 0 && (
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      {editingSpot.reference_images.map((imgUrl, idx) => {
                        // Handle both full URLs and relative paths
                        const imageSource = imgUrl.startsWith('http') ? imgUrl : `${BACKEND_URL}${imgUrl}`;
                        console.log(`Reference image ${idx + 1}: ${imgUrl} -> ${imageSource}`);
                        
                        return (
                        <div key={`${imgUrl}-${idx}`} className="relative group border rounded overflow-hidden bg-gray-100">
                          <img 
                            src={imageSource} 
                            alt={`Reference ${idx + 1}`}
                            className="w-full h-32 object-cover"
                            onError={(e) => {
                              console.error('Failed to load image:', imageSource);
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EImage Error%3C/text%3E%3C/svg%3E';
                            }}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                            <button
                              onClick={() => {
                                const fullUrl = `${window.location.origin}${API}${imgUrl}`;
                                navigator.clipboard.writeText(fullUrl);
                                toast.success('📋 Image URL copied!');
                              }}
                              className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
                            >
                              Copy URL
                            </button>
                            <button
                              onClick={async () => {
                                if (confirm('Delete this image?')) {
                                  try {
                                    // Refetch the spot to get current state before deletion
                                    const spotResponse = await axios.get(
                                      `${API}/spots/${editingSpot.id}`,
                                      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                                    );
                                    const currentSpot = spotResponse.data;
                                    
                                    // Find the current index of this image URL
                                    const currentIndex = currentSpot.reference_images.findIndex(url => url === imgUrl);
                                    
                                    if (currentIndex === -1) {
                                      toast.error('Image not found');
                                      return;
                                    }
                                    
                                    // Delete using the current index
                                    await axios.delete(
                                      `${API}/spots/${editingSpot.id}/images/${currentIndex}`,
                                      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                                    );
                                    
                                    // Refetch the spot data to get updated reference_images array
                                    const updatedSpotResponse = await axios.get(
                                      `${API}/spots/${editingSpot.id}`,
                                      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                                    );
                                    setEditingSpot(updatedSpotResponse.data);
                                    toast.success('Image removed from spot');
                                  } catch (error) {
                                    console.error('Error deleting image:', error);
                                    toast.error('Failed to delete image');
                                  }
                                }
                              }}
                              className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )})}
                    </div>
                  )}
                  
                  {/* Upload New Image */}
                  <div className="border-2 border-dashed border-gray-300 rounded p-4 text-center hover:border-gray-400 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            
                            const response = await axios.post(
                              `${API}/spots/${editingSpot.id}/upload-image`,
                              formData,
                              { 
                                headers: { 
                                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                                  'Content-Type': 'multipart/form-data'
                                } 
                              }
                            );
                            
                            const newImages = [...(editingSpot.reference_images || []), response.data.image_url];
                            setEditingSpot({...editingSpot, reference_images: newImages});
                            toast.success('📸 Image uploaded successfully!');
                            e.target.value = ''; // Reset input
                          } catch (error) {
                            console.error('Error uploading image:', error);
                            toast.error('Failed to upload image');
                          }
                        }
                      }}
                      className="hidden"
                      id="spot-image-upload"
                    />
                    <label 
                      htmlFor="spot-image-upload" 
                      className="cursor-pointer text-sm text-gray-600 hover:text-gray-800"
                    >
                      <div className="flex flex-col items-center">
                        <svg className="w-8 h-8 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span className="font-medium">Click to upload image</span>
                        <span className="text-xs text-gray-500 mt-1">PNG, JPG, GIF, WebP up to 10MB</span>
                      </div>
                    </label>
                  </div>
                  
                  {/* Select from Library */}
                  <div className="mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => setShowLibrarySelector(true)}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Select from Image Library
                    </Button>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowEditDialog(false);
                      setEditingSpot(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        await axios.put(
                          `${API}/spots/${editingSpot.id}`,
                          editingSpot,
                          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                        );
                        toast.success('✅ Spot updated successfully!');
                        setShowEditDialog(false);
                        setEditingSpot(null);
                        fetchEventData();
                      } catch (error) {
                        console.error('Error updating spot:', error);
                        toast.error('Failed to update spot');
                      }
                    }}
                    className="bg-emerald-500 hover:bg-emerald-600"
                  >
                    💾 Save Changes
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>


        {/* Image Library Selector Dialog */}
        <Dialog open={showLibrarySelector} onOpenChange={setShowLibrarySelector}>
          <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>📚 Select Images from Library</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Search and Filter */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="🔍 Search images..."
                    value={librarySearchTerm}
                    onChange={(e) => setLibrarySearchTerm(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <select
                  value={selectedLibraryPack}
                  onChange={(e) => setSelectedLibraryPack(e.target.value)}
                  className="border rounded px-3 py-2"
                >
                  <option value="All">All Packs</option>
                  {libraryPacks.map(pack => (
                    <option key={pack.id} value={pack.name}>
                      {pack.name} ({pack.image_count || 0})
                    </option>
                  ))}
                </select>
              </div>

              {/* Selected Count */}
              {selectedLibraryImages.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded px-4 py-2 text-sm">
                  ✅ {selectedLibraryImages.length} image(s) selected
                </div>
              )}

              {/* Images Grid */}
              <div className="grid grid-cols-4 gap-3">
                {libraryImages
                  .filter(img => {
                    const matchesPack = selectedLibraryPack === 'All' || img.pack === selectedLibraryPack;
                    const matchesSearch = !librarySearchTerm || 
                      img.name.toLowerCase().includes(librarySearchTerm.toLowerCase()) ||
                      img.pack.toLowerCase().includes(librarySearchTerm.toLowerCase());
                    return matchesPack && matchesSearch;
                  })
                  .map(image => {
                    const isSelected = selectedLibraryImages.includes(image.id);
                    return (
                      <div
                        key={image.id}
                        onClick={() => toggleLibraryImageSelection(image.id)}
                        className={`relative cursor-pointer border-2 rounded overflow-hidden transition-all ${
                          isSelected ? 'border-blue-500 ring-2 ring-blue-300' : 'border-gray-200 hover:border-gray-400'
                        }`}
                      >
                        <img
                          src={`${BACKEND_URL}${image.image_url}`}
                          alt={image.name}
                          className="w-full h-32 object-cover"
                        />
                        {isSelected && (
                          <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center">
                            ✓
                          </div>
                        )}
                        <div className="p-2 bg-white">
                          <p className="text-xs font-medium truncate">{image.name}</p>
                          <p className="text-xs text-gray-500">{image.pack}</p>
                        </div>
                      </div>
                    );
                  })}
              </div>

              {libraryImages.filter(img => {
                const matchesPack = selectedLibraryPack === 'All' || img.pack === selectedLibraryPack;
                const matchesSearch = !librarySearchTerm || 
                  img.name.toLowerCase().includes(librarySearchTerm.toLowerCase()) ||
                  img.pack.toLowerCase().includes(librarySearchTerm.toLowerCase());
                return matchesPack && matchesSearch;
              }).length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No images found. Try adjusting your filters.
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowLibrarySelector(false);
                    setSelectedLibraryImages([]);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddLibraryImages}
                  disabled={selectedLibraryImages.length === 0}
                >
                  Add {selectedLibraryImages.length > 0 ? `(${selectedLibraryImages.length})` : ''} to Spot
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </div>
  );
}


// Event-wide gallery used by the Photos tab. Shows every high-res photo
// uploaded across all spots, grouped by spot. Photo editors land here as
// their only view of an event.
function EventPhotosGallery({ eventId, canDelete }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zipping, setZipping] = useState(false);
  const [filter, setFilter] = useState('');
  const [lightbox, setLightbox] = useState({ open: false, list: [], index: 0 });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/events/${eventId}/photos`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setPhotos(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load photos');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { refresh(); }, [refresh]);

  const fmtBytes = (n) => {
    if (!n) return '';
    if (n < 1024) return `${n}B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
    return `${(n / 1024 / 1024).toFixed(1)}MB`;
  };

  const remove = async (photo) => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      await axios.delete(`${API}/spot-photos/${photo.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
      setLightbox((lb) => {
        if (!lb.open) return lb;
        const list = lb.list.filter((p) => p.id !== photo.id);
        if (list.length === 0) return { open: false, list: [], index: 0 };
        return { ...lb, list, index: Math.min(lb.index, list.length - 1) };
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove');
    }
  };

  const saveNote = async (photo, note) => {
    try {
      const r = await axios.patch(
        `${API}/spot-photos/${photo.id}`,
        { note },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      const updated = r.data;
      setPhotos((prev) => prev.map((p) => (p.id === photo.id ? { ...p, note: updated.note } : p)));
      setLightbox((lb) => ({
        ...lb,
        list: lb.list.map((p) => (p.id === photo.id ? { ...p, note: updated.note } : p)),
      }));
      toast.success('Note saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save note');
    }
  };

  const downloadAll = async () => {
    setZipping(true);
    try {
      const r = await axios.get(`${API}/events/${eventId}/photos/download`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const blob = new Blob([r.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `event-photos-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not build ZIP');
    } finally {
      setZipping(false);
    }
  };

  const filtered = useMemo(() => {
    const lower = filter.trim().toLowerCase();
    if (!lower) return photos;
    return photos.filter((p) => {
      const hay = `${p.spot_name || ''} ${p.photographer_name || ''} ${p.note || ''} ${p.filename || ''}`.toLowerCase();
      return hay.includes(lower);
    });
  }, [photos, filter]);

  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const p of filtered) {
      const key = p.spot_id || 'unknown';
      if (!buckets.has(key)) {
        buckets.set(key, {
          spot_id: key,
          spot_name: p.spot_name || 'Unknown spot',
          spot_address: p.spot_address,
          spot_scheduled_time: p.spot_scheduled_time,
          photos: [],
        });
      }
      buckets.get(key).photos.push(p);
    }
    return Array.from(buckets.values());
  }, [filtered]);

  const latest = useMemo(() => filtered.slice(0, 12), [filtered]);

  const totalCount = photos.length;
  const totalBytes = photos.reduce((sum, p) => sum + (p.size_bytes || 0), 0);

  const openLightbox = (list, photo) => {
    const idx = list.findIndex((p) => p.id === photo.id);
    if (idx >= 0) setLightbox({ open: true, list, index: idx });
  };

  return (
    <Card className="glass-card p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-semibold">High-res photos</h2>
          <p className="text-sm text-slate-500">
            {totalCount} photo{totalCount === 1 ? '' : 's'}
            {totalBytes > 0 ? ` · ${fmtBytes(totalBytes)} total` : ''}
            {' '}— uploaded by photographers via the share link.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by spot, photographer, note…"
            className="w-64"
          />
          <Button variant="outline" onClick={refresh} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </Button>
          <Button onClick={downloadAll} disabled={zipping || totalCount === 0}>
            {zipping ? 'Building ZIP…' : 'Download all (ZIP)'}
          </Button>
        </div>
      </div>

      {totalCount === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Camera className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No high-res photos yet.</p>
          <p className="text-xs mt-1">Photographers can attach originals from the share link, after they check out of a spot.</p>
        </div>
      ) : grouped.length === 0 ? (
        <p className="text-center py-8 text-slate-500 text-sm">No matches for "{filter}".</p>
      ) : (
        <>
          {/* Latest strip — always shows the most recent uploads at the top. */}
          {latest.length > 0 && (
            <div className="border rounded-lg p-3 mb-4 bg-emerald-50/40">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium text-sm">🆕 Latest uploads</h3>
                <span className="text-xs text-slate-500">Most recent first</span>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {latest.map((p) => {
                  const fullUrl = p.url?.startsWith('http') ? p.url : `${BACKEND_URL}${p.url}`;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => openLightbox(filtered, p)}
                      className="relative flex-shrink-0 w-24 h-24 rounded overflow-hidden border bg-white hover:ring-2 hover:ring-emerald-500"
                    >
                      <img src={fullUrl} alt="" className="w-full h-full object-cover" />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1 py-0.5">
                        <p className="truncate">{p.photographer_name || '—'}</p>
                      </div>
                      {p.note && (
                        <div className="absolute top-0.5 right-0.5 bg-amber-400/90 rounded-sm w-4 h-4 flex items-center justify-center text-[8px] text-amber-900">📝</div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-6">
            {grouped.map((g) => (
              <div key={g.spot_id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-medium">{g.spot_name}</h3>
                    <p className="text-xs text-slate-500">
                      {g.spot_scheduled_time && <span>{g.spot_scheduled_time} · </span>}
                      {g.spot_address || ''}
                      <span className="ml-2">· {g.photos.length} photo{g.photos.length === 1 ? '' : 's'}</span>
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {g.photos.map((p) => {
                    const fullUrl = p.url?.startsWith('http') ? p.url : `${BACKEND_URL}${p.url}`;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => openLightbox(g.photos, p)}
                        className="relative group border rounded overflow-hidden bg-slate-50 text-left hover:ring-2 hover:ring-emerald-500"
                      >
                        <img src={fullUrl} alt="" className="w-full h-32 object-cover" />
                        {p.note && (
                          <div className="absolute top-1 right-1 bg-amber-400/90 rounded p-0.5" title={p.note}>📝</div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5 flex items-center justify-between">
                          <span className="truncate">{p.photographer_name || '—'}</span>
                          <span>{fmtBytes(p.size_bytes)}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {lightbox.open && (
        <PhotoLightbox
          photos={lightbox.list}
          index={lightbox.index}
          onClose={() => setLightbox({ open: false, list: [], index: 0 })}
          onPrev={() => setLightbox((lb) => ({ ...lb, index: Math.max(0, lb.index - 1) }))}
          onNext={() => setLightbox((lb) => ({ ...lb, index: Math.min(lb.list.length - 1, lb.index + 1) }))}
          canEditNote={canDelete}
          onSaveNote={saveNote}
          canDelete={canDelete}
          onDelete={remove}
        />
      )}
    </Card>
  );
}
